import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

// UI Components
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Badge } from "./components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { Switch } from "./components/ui/switch";

// Icons
import { 
  Flame, Shield, LogOut, Eye, EyeOff, Users, Settings, 
  BarChart3, CheckCircle2, XCircle, Plus, Trash2, Edit2,
  Key, Building2, User, HelpCircle, Save, X, Truck, Copy, RefreshCw,
  Volume2, Bell, BellOff, Filter, Activity, Clock, TrendingUp, AlertTriangle, Mail,
  Lock, Unlock, ChevronUp, ChevronDown, Home, ExternalLink, Wifi, Wrench
} from "lucide-react";

// Table Components
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Notifications Settings Component
function NotificationsSettings({ settings, authHeader, fetchData, isAdmin }) {
  const [iobrokerUrl, setIobrokerUrl] = useState(settings?.iobroker_url || '');
  const [alexaSpeak, setAlexaSpeak] = useState(settings?.iobroker_alexa_speak || '');
  const [alexaNotifyMethod, setAlexaNotifyMethod] = useState(settings?.alexa_notify_method || 'none');
  const [alexaNotifyMeCode, setAlexaNotifyMeCode] = useState(settings?.alexa_notify_me_access_code || '');
  const [alexaMessageTemplate, setAlexaMessageTemplate] = useState(settings?.alexa_message_template || '');
  const [saving, setSaving] = useState(false);
  const [testStatus, setTestStatus] = useState('');

  useEffect(() => {
    setIobrokerUrl(settings?.iobroker_url || '');
    setAlexaSpeak(settings?.iobroker_alexa_speak || '');
    setAlexaNotifyMethod(settings?.alexa_notify_method || 'none');
    setAlexaNotifyMeCode(settings?.alexa_notify_me_access_code || '');
    setAlexaMessageTemplate(settings?.alexa_message_template || '');
  }, [settings]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/settings/`, {
        iobroker_url: iobrokerUrl,
        iobroker_alexa_speak: alexaSpeak,
        alexa_notify_method: alexaNotifyMethod,
        alexa_notify_me_access_code: alexaNotifyMeCode,
        alexa_message_template: alexaMessageTemplate
      }, { headers: authHeader() });
      toast.success('Einstellungen gespeichert');
      fetchData();
    } catch (e) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    if (!iobrokerUrl) {
      toast.error('ioBroker URL ist erforderlich');
      return;
    }

    setTestStatus('testing');
    try {
      // Try to reach ioBroker
      const testUrl = `${iobrokerUrl.rstrip ? iobrokerUrl.rstrip('/') : iobrokerUrl.replace(/\/$/, '')}/getPlainValue/system.adapter.admin.0.alive`;
      const response = await axios.get(testUrl, { timeout: 5000 });
      setTestStatus('success');
      toast.success('Verbindung erfolgreich!');
    } catch (e) {
      setTestStatus('error');
      toast.error('Verbindung fehlgeschlagen. Ist ioBroker erreichbar?');
    }
  };

  return (
    <div className="space-y-6">
      {/* ioBroker/Alexa */}
      <div>
        <h3 className="font-medium mb-2 flex items-center gap-2">
          <Volume2 className="h-5 w-5 text-[#FF8C00]" />
          ioBroker / Alexa Benachrichtigung
        </h3>
        <p className="text-sm text-zinc-500 mb-4">
          Bei einem neuen Alarm wird eine Sprachansage über Alexa ausgegeben.
        </p>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>ioBroker Simple API URL</Label>
            <Input
              value={iobrokerUrl}
              onChange={(e) => setIobrokerUrl(e.target.value)}
              placeholder="http://192.168.178.76:8087"
              className="bg-[#1F2937] border-[#1F2937]/80"
              disabled={!isAdmin}
            />
            <p className="text-xs text-zinc-600">
              Die URL des ioBroker Simple API Adapters (Port 8087)
            </p>
          </div>

          <div className="space-y-2">
            <Label>Alexa Speak Object</Label>
            <Input
              value={alexaSpeak}
              onChange={(e) => setAlexaSpeak(e.target.value)}
              placeholder="alexa2.0.Echo-Devices.XXXXX.Commands.speak"
              className="bg-[#1F2937] border-[#1F2937]/80"
              disabled={!isAdmin}
            />
            <p className="text-xs text-zinc-600">
              Der Datenpunkt für die Alexa Sprachausgabe
            </p>
          </div>

          {isAdmin && (
            <div className="flex gap-2">
              <Button onClick={testConnection} variant="outline" size="sm" disabled={!iobrokerUrl}>
                {testStatus === 'testing' ? 'Teste...' : 'Verbindung testen'}
              </Button>
              <Button onClick={saveSettings} size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Speichern...' : 'Speichern'}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Alexa Notify Me - Direct */}
      <div className="p-4 bg-purple-900/20 rounded border border-purple-800">
        <h3 className="font-medium mb-2 flex items-center gap-2">
          <Volume2 className="h-5 w-5 text-purple-500" />
          Alexa Direkt-Benachrichtigung
        </h3>
        <p className="text-sm text-zinc-500 mb-4">
          Wählen Sie, wie Sie bei einem Alarm benachrichtigt werden möchten.
        </p>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Benachrichtigungsmethode</Label>
            <Select value={alexaNotifyMethod} onValueChange={setAlexaNotifyMethod}>
              <SelectTrigger className="w-full bg-[#1F2937] border-[#1F2937]/80">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1F2937]/80 border-[#1F2937]/80">
                <SelectItem value="none">Keine Alexa-Benachrichtigung</SelectItem>
                <SelectItem value="home_assistant">Home Assistant (erfordert HA-Integration)</SelectItem>
                <SelectItem value="notify_me">Alexa &quot;Notify Me&quot; Skill (direkt, ohne HA)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {alexaNotifyMethod === 'notify_me' && (
            <div className="space-y-3 p-3 bg-[#1F2937]/50 rounded border border-purple-700/50">
              <p className="text-sm text-purple-300">
                <strong>Einrichtung:</strong> Aktivieren Sie den kostenlosen &quot;Notify Me&quot; Skill in der Alexa App und kopieren Sie den Access Code.
              </p>
              <div className="space-y-2">
                <Label>Notify Me Access Code</Label>
                <Input
                  type="password"
                  value={alexaNotifyMeCode}
                  onChange={(e) => setAlexaNotifyMeCode(e.target.value)}
                  placeholder="amzn1.ask.account.xxxxx"
                  className="bg-[#1F2937] border-[#1F2937]/80"
                />
                <p className="text-xs text-zinc-500">
                  Den Access Code finden Sie in der Alexa App unter &quot;Notify Me&quot; Skill Einstellungen.
                </p>
              </div>
            </div>
          )}

          {alexaNotifyMethod === 'home_assistant' && (
            <div className="p-3 bg-[#DC2626]/10 rounded border border-[#DC2626]/50/50">
              <p className="text-sm text-[#FFD700]">
                Die Alexa-Benachrichtigung wird über Home Assistant gesteuert. 
                Konfigurieren Sie die Alexa-Einstellungen in der Einsatz-Monitor Integration in Home Assistant.
              </p>
            </div>
          )}

          {alexaNotifyMethod && alexaNotifyMethod !== 'none' && (
            <div className="space-y-2">
              <Label>Ansage-Text (optional)</Label>
              <Input
                value={alexaMessageTemplate}
                onChange={(e) => setAlexaMessageTemplate(e.target.value)}
                placeholder="Achtung Feuerwehreinsatz! Stichwort: {keyword}. Fahrzeuge: {vehicles}"
                className="bg-[#1F2937] border-[#1F2937]/80"
              />
              <p className="text-xs text-zinc-500">
                Platzhalter: {'{keyword}'}, {'{unit}'}, {'{vehicles}'}
              </p>
            </div>
          )}

          <Button onClick={saveSettings} size="sm" className="bg-purple-600 hover:bg-purple-700" disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Speichern...' : 'Speichern'}
          </Button>
        </div>
      </div>

      {/* Info Box */}
      <div className="p-4 bg-[#DC2626]/10 rounded border border-[#DC2626]/50 text-sm space-y-2">
        <p className="text-[#FF8C00] font-medium">Voraussetzungen für ioBroker:</p>
        <ul className="list-disc list-inside space-y-1 text-zinc-400">
          <li>ioBroker mit installiertem Simple API Adapter</li>
          <li>Alexa2 Adapter für Sprachausgabe</li>
          <li>Netzwerkzugriff vom Server auf ioBroker</li>
        </ul>
      </div>

      {/* Test Info */}
      <div className="p-4 bg-[#1F2937]/80/50 rounded border border-[#1F2937]">
        <p className="text-sm text-zinc-500 mb-2">Bei einem Alarm wird folgender Text gesprochen:</p>
        <code className="text-xs text-[#FFD700] block p-2 bg-[#1F2937] rounded">
          &quot;Achtung Feuerwehreinsatz! Stichwort: [STICHWORT]. Fahrzeuge: [FAHRZEUGE].&quot;
        </code>
      </div>
    </div>
  );
}

// Settings Panel Component
function SettingsPanel({ settings, webhookKey, currentUser, authHeader, copyToClipboard, regenerateWebhookKey, fetchData }) {
  const [settingsTab, setSettingsTab] = useState('webhook');
  const [newFunction, setNewFunction] = useState('');
  const [newVehicle, setNewVehicle] = useState('');
  const [ausrueckeordnung, setAusrueckeordnung] = useState([]);
  const [newAoKeyword, setNewAoKeyword] = useState('');
  const [newAoVehicles, setNewAoVehicles] = useState('');
  const [editingAo, setEditingAo] = useState(null);
  const [keywordReplacements, setKeywordReplacements] = useState([]);
  const [newKrKeyword, setNewKrKeyword] = useState('');
  const [newKrReplacement, setNewKrReplacement] = useState('');
  const [haToken, setHaToken] = useState('');
  const [tenantKeywords, setTenantKeywords] = useState([]);
  const isAdmin = currentUser?.role === 'super_admin';

  // Fetch ausrückeordnung and tenant keywords
  useEffect(() => {
    const fetchAo = async () => {
      try {
        const res = await axios.get(`${API}/api/settings/ausrueckeordnung`, { headers: authHeader() });
        setAusrueckeordnung(res.data);
      } catch (e) { console.error(e); }
    };
    const fetchKr = async () => {
      try {
        const res = await axios.get(`${API}/api/settings/keyword-replacements`, { headers: authHeader() });
        setKeywordReplacements(res.data);
      } catch (e) { console.error(e); }
    };
    const fetchTenantKeywords = async () => {
      try {
        const res = await axios.get(`${API}/api/settings/tenant-keywords`, { headers: authHeader() });
        setTenantKeywords(res.data.keywords || []);
      } catch (e) { console.error(e); }
    };
    fetchAo();
    fetchKr();
    fetchTenantKeywords();
  }, [authHeader]);

  // Generate HA Token
  const generateHAToken = async () => {
    try {
      const res = await axios.post(`${API}/api/ha/token/generate`, {}, { headers: authHeader() });
      setHaToken(res.data.token);
      toast.success('Home Assistant Token generiert!');
    } catch (e) {
      toast.error('Fehler beim Generieren des Tokens');
    }
  };

  // Add function
  const addFunction = async () => {
    if (!newFunction.trim()) return;
    const functions = [...(settings?.available_functions || []), newFunction.trim()];
    try {
      await axios.put(`${API}/api/settings/`, { available_functions: functions }, { headers: authHeader() });
      toast.success('Funktion hinzugefügt');
      setNewFunction('');
      fetchData();
    } catch (e) { toast.error('Fehler'); }
  };

  // Remove function
  const removeFunction = async (func) => {
    const functions = (settings?.available_functions || []).filter(f => f !== func);
    try {
      await axios.put(`${API}/api/settings/`, { available_functions: functions }, { headers: authHeader() });
      toast.success('Funktion entfernt');
      fetchData();
    } catch (e) { toast.error('Fehler'); }
  };

  // Add vehicle
  const addVehicle = async () => {
    if (!newVehicle.trim()) return;
    const vehicles = [...(settings?.available_vehicles || []), newVehicle.trim()];
    try {
      await axios.put(`${API}/api/settings/`, { available_vehicles: vehicles }, { headers: authHeader() });
      toast.success('Fahrzeug hinzugefügt');
      setNewVehicle('');
      fetchData();
    } catch (e) { toast.error('Fehler'); }
  };

  // Remove vehicle
  const removeVehicle = async (veh) => {
    const vehicles = (settings?.available_vehicles || []).filter(v => v !== veh);
    try {
      await axios.put(`${API}/api/settings/`, { available_vehicles: vehicles }, { headers: authHeader() });
      toast.success('Fahrzeug entfernt');
      fetchData();
    } catch (e) { toast.error('Fehler'); }
  };

  // Add Ausrückeordnung
  const addAusrueckeordnung = async () => {
    if (!newAoKeyword.trim() || !newAoVehicles.trim()) return;
    try {
      await axios.post(`${API}/api/settings/ausrueckeordnung?keyword=${encodeURIComponent(newAoKeyword)}&vehicles=${encodeURIComponent(newAoVehicles)}`, {}, { headers: authHeader() });
      toast.success('Eintrag hinzugefügt');
      setNewAoKeyword('');
      setNewAoVehicles('');
      const res = await axios.get(`${API}/api/settings/ausrueckeordnung`, { headers: authHeader() });
      setAusrueckeordnung(res.data);
    } catch (e) { toast.error('Fehler'); }
  };

  // Delete Ausrückeordnung
  const deleteAusrueckeordnung = async (id) => {
    try {
      await axios.delete(`${API}/api/settings/ausrueckeordnung/${id}`, { headers: authHeader() });
      toast.success('Eintrag gelöscht');
      setAusrueckeordnung(prev => prev.filter(a => a.id !== id));
    } catch (e) { toast.error('Fehler'); }
  };

  // Add Keyword Replacement
  const addKeywordReplacement = async () => {
    if (!newKrKeyword.trim() || !newKrReplacement.trim()) return;
    try {
      await axios.post(`${API}/api/settings/keyword-replacements?keyword=${encodeURIComponent(newKrKeyword)}&replacement=${encodeURIComponent(newKrReplacement)}`, {}, { headers: authHeader() });
      toast.success('Ersetzung hinzugefügt');
      setNewKrKeyword('');
      setNewKrReplacement('');
      const res = await axios.get(`${API}/api/settings/keyword-replacements`, { headers: authHeader() });
      setKeywordReplacements(res.data);
    } catch (e) { toast.error('Fehler'); }
  };

  // Delete Keyword Replacement
  const deleteKeywordReplacement = async (id) => {
    try {
      await axios.delete(`${API}/api/settings/keyword-replacements/${id}`, { headers: authHeader() });
      toast.success('Ersetzung gelöscht');
      setKeywordReplacements(prev => prev.filter(k => k.id !== id));
    } catch (e) { toast.error('Fehler'); }
  };

  return (
    <Card className="border-[#1F2937] bg-black/40">
      <CardContent className="p-0">
        <Tabs value={settingsTab} onValueChange={setSettingsTab}>
          <TabsList className="w-full grid grid-cols-4 bg-[#1F2937]/80 rounded-none border-b border-[#1F2937]">
            <TabsTrigger value="webhook" className="data-[state=active]:bg-[#1F2937]">
              <Key className="h-4 w-4 mr-2" />Webhook
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-[#1F2937]">
              <Volume2 className="h-4 w-4 mr-2" />Benachrichtigung
            </TabsTrigger>
            <TabsTrigger value="sprache" className="data-[state=active]:bg-[#1F2937]">
              <Settings className="h-4 w-4 mr-2" />Sprachausgabe
            </TabsTrigger>
            <TabsTrigger value="homeassistant" className="data-[state=active]:bg-[#1F2937]">
              <Home className="h-4 w-4 mr-2" />Home Assistant
            </TabsTrigger>
          </TabsList>

          {/* Webhook Tab */}
          <TabsContent value="webhook" className="p-6 space-y-4">
            <div>
              <h3 className="font-medium mb-2">Webhook URL für Alamos aPager</h3>
              <div className="p-3 bg-[#1F2937]/80 rounded border border-[#1F2937]">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-sm">Vollständige URL:</span>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(`${window.location.origin}/api/webhook/alarm?key=${webhookKey}`)}>
                      <Copy className="h-4 w-4 mr-1" /> Kopieren
                    </Button>
                  </div>
                  <code className="text-[#FFD700] text-sm break-all select-all bg-[#1F2937] p-2 rounded">
                    {window.location.origin}/api/webhook/alarm?key={webhookKey || '...'}
                  </code>
                </div>
              </div>
              <div className="mt-3 p-3 bg-[#1F2937]/80 rounded border border-[#1F2937]">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-sm">Nur Webhook-Key:</span>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(webhookKey)}>
                      <Copy className="h-4 w-4 mr-1" /> Kopieren
                    </Button>
                  </div>
                  <code className="text-green-400 text-sm break-all select-all bg-[#1F2937] p-2 rounded font-mono">
                    {webhookKey || 'Wird geladen...'}
                  </code>
                </div>
              </div>
            </div>
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={regenerateWebhookKey} className="border-amber-700 text-[#FFD700]">
                <RefreshCw className="h-4 w-4 mr-2" />Neuen Key generieren
              </Button>
            )}
            <div className="p-4 bg-[#DC2626]/10 rounded border border-[#DC2626]/50 text-sm">
              <p className="text-[#FF8C00] font-medium mb-2">Anleitung:</p>
              <ol className="list-decimal list-inside space-y-1 text-zinc-400">
                <li>Kopieren Sie die Webhook URL</li>
                <li>Öffnen Sie Ihren Alamos aPager</li>
                <li>Gehen Sie zu Einstellungen → Webhooks</li>
                <li>Fügen Sie die URL als neuen Webhook hinzu</li>
              </ol>
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="p-6 space-y-6">
            <NotificationsSettings settings={settings} authHeader={authHeader} fetchData={fetchData} isAdmin={isAdmin} />
          </TabsContent>

          {/* Sprachausgabe Tab */}
          <TabsContent value="sprache" className="p-6 space-y-4">
            <div>
              <h3 className="font-medium mb-2">Sprachausgabe-Ersetzungen</h3>
              <p className="text-sm text-zinc-500 mb-4">
                Definieren Sie, wie Stichwörter bei der Alexa-Sprachausgabe ausgesprochen werden sollen.
              </p>
            </div>

            {/* Stichwörter ohne Ersetzung */}
            {tenantKeywords.length > 0 && (
              <div className="p-4 bg-[#1F2937]/80/50 rounded border border-red-800/50">
                <h4 className="text-sm font-medium text-[#DC2626] mb-3 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Stichwörter ohne Ersetzung ({tenantKeywords.filter(kw => !keywordReplacements.some(kr => kr.keyword.toLowerCase() === kw.toLowerCase())).length})
                </h4>
                {tenantKeywords.filter(kw => !keywordReplacements.some(kr => kr.keyword.toLowerCase() === kw.toLowerCase())).length === 0 ? (
                  <p className="text-zinc-500 text-sm py-2 text-center">
                    Alle Stichwörter haben Ersetzungen ✓
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {tenantKeywords.filter(kw => !keywordReplacements.some(kr => kr.keyword.toLowerCase() === kw.toLowerCase())).map((kw) => (
                      <Button
                        key={kw}
                        variant="outline"
                        size="sm"
                        className="border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626]/20"
                        onClick={() => {
                          setNewKrKeyword(kw);
                          setNewKrReplacement('');
                        }}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        {kw}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Add new */}
            <div className="p-4 bg-[#1F2937]/80/50 rounded border border-[#1F2937] space-y-3">
              <p className="text-sm font-medium text-[#FF8C00] flex items-center gap-2">
                <Plus className="h-4 w-4" />
                {newKrKeyword ? `Ersetzung für "${newKrKeyword}"` : 'Neue Ersetzung'}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  value={newKrKeyword}
                  onChange={(e) => setNewKrKeyword(e.target.value)}
                  placeholder="Original (z.B. B1)"
                  className="bg-[#1F2937] border-[#1F2937]/80"
                />
                <Input
                  value={newKrReplacement}
                  onChange={(e) => setNewKrReplacement(e.target.value)}
                  placeholder="Ersetzung (z.B. Brand 1)"
                  className="bg-[#1F2937] border-[#1F2937]/80"
                />
              </div>
              <Button onClick={addKeywordReplacement} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="h-4 w-4 mr-2" />Hinzufügen
              </Button>
            </div>

            {/* Stichwörter mit Ersetzung */}
            <div className="p-4 bg-[#1F2937]/80/50 rounded border border-emerald-800/50">
              <h4 className="text-sm font-medium text-emerald-400 mb-3 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Stichwörter mit Ersetzung ({keywordReplacements.length})
              </h4>
              {keywordReplacements.length === 0 ? (
                <p className="text-zinc-500 text-center py-4">Keine Ersetzungen vorhanden</p>
              ) : (
                <div className="space-y-2">
                  {keywordReplacements.map((kr) => (
                    <div key={kr.id} className="flex items-center justify-between p-3 bg-[#1F2937]/50 rounded border border-[#1F2937]/80">
                      <div className="flex items-center gap-4">
                        <code className="bg-zinc-700 px-2 py-1 rounded text-sm font-mono">{kr.keyword}</code>
                        <span className="text-zinc-400">→</span>
                        <span className="text-emerald-400">&quot;{kr.replacement}&quot;</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => deleteKeywordReplacement(kr.id)} className="text-[#DC2626] hover:text-red-300">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Home Assistant Tab */}
          <TabsContent value="homeassistant" className="p-6 space-y-6">
            <div>
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <Home className="h-5 w-5 text-[#FF8C00]" />
                Home Assistant Integration
              </h3>
              <p className="text-sm text-zinc-500 mb-4">
                Verbinden Sie Ihren Home Assistant mit dem Einsatz-Monitor, um bei Alarmen automatisch Aktionen auszulösen.
              </p>
            </div>

            {/* Token generieren */}
            <div className="p-4 bg-[#1F2937]/80/50 rounded border border-[#1F2937]">
              <h4 className="font-medium mb-3">API Token für Home Assistant</h4>
              {haToken ? (
                <div className="space-y-3">
                  <div className="p-3 bg-[#1F2937] rounded border border-[#1F2937]/80">
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-[#FFD700] text-sm break-all font-mono">{haToken}</code>
                      <Button variant="ghost" size="sm" onClick={() => {
                        navigator.clipboard.writeText(haToken);
                        toast.success("Token kopiert!");
                      }}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-zinc-500">
                    ⚠️ Speichern Sie diesen Token sicher! Er wird nur einmal angezeigt.
                  </p>
                </div>
              ) : (
                <Button onClick={generateHAToken} className="bg-[#DC2626] hover:bg-[#DC2626]/90">
                  <Key className="h-4 w-4 mr-2" />Token generieren
                </Button>
              )}
            </div>

            {/* HACS Installation */}
            <div className="p-4 bg-[#DC2626]/10 rounded border border-[#DC2626]/50">
              <h4 className="font-medium text-[#FF8C00] mb-3">Installation in Home Assistant</h4>
              <div className="text-sm text-zinc-400 space-y-2">
                <p><strong>Option 1: Via HACS (empfohlen)</strong></p>
                <ol className="list-decimal list-inside space-y-1 pl-4">
                  <li>Öffnen Sie HACS in Home Assistant</li>
                  <li>Klicken Sie auf "Integrationen" → Drei Punkte → "Benutzerdefinierte Repositories"</li>
                  <li>Fügen Sie die Repository-URL hinzu und wählen Sie "Integration"</li>
                  <li>Installieren Sie "Einsatz-Monitor" und starten Sie Home Assistant neu</li>
                </ol>
                
                <p className="mt-4"><strong>Option 2: Manuelle Installation</strong></p>
                <ol className="list-decimal list-inside space-y-1 pl-4">
                  <li>Laden Sie die Integration von GitHub herunter</li>
                  <li>Kopieren Sie <code className="text-[#FFD700]">custom_components/einsatz_monitor</code> in Ihr HA config-Verzeichnis</li>
                  <li>Starten Sie Home Assistant neu</li>
                </ol>
              </div>
            </div>

            {/* Konfiguration */}
            <div className="p-4 bg-[#1F2937]/80/50 rounded border border-[#1F2937]">
              <h4 className="font-medium mb-3">Konfiguration in Home Assistant</h4>
              <div className="text-sm text-zinc-400 space-y-2">
                <p>Nach der Installation:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Gehen Sie zu Einstellungen → Integrationen → Integration hinzufügen</li>
                  <li>Suchen Sie nach "Einsatz-Monitor"</li>
                  <li>Geben Sie ein:
                    <ul className="list-disc list-inside ml-4 mt-1">
                      <li><strong>Server URL:</strong> {window.location.origin}</li>
                      <li><strong>API Token:</strong> Der oben generierte Token</li>
                    </ul>
                  </li>
                </ol>
              </div>
            </div>

            {/* Sensoren & Events */}
            <div className="p-4 bg-emerald-900/20 rounded border border-emerald-800">
              <h4 className="font-medium text-emerald-400 mb-3">Verfügbare Sensoren & Events</h4>
              <div className="text-sm text-zinc-400">
                <p className="mb-2"><strong>Sensoren:</strong></p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li><code className="text-[#FFD700]">sensor.einsatze_24h</code> - Anzahl Einsätze (24h)</li>
                  <li><code className="text-[#FFD700]">sensor.letzter_einsatz_stichwort</code> - Stichwort</li>
                  <li><code className="text-[#FFD700]">sensor.letzter_einsatz_fahrzeuge</code> - Fahrzeuge</li>
                  <li><code className="text-[#FFD700]">binary_sensor.einsatz_aktiv</code> - Aktiv wenn &lt;30 Min.</li>
                </ul>
                
                <p className="mt-3 mb-2"><strong>Event für Automationen:</strong></p>
                <pre className="p-2 bg-[#1F2937]/80 rounded text-xs overflow-x-auto">
{`trigger:
  - platform: event
    event_type: einsatz_monitor_new_alarm
action:
  - service: notify.mobile_app_handy
    data:
      title: "🚨 ALARM"
      message: "{{ trigger.event.data.keyword }}"`}
                </pre>
              </div>
            </div>

            {/* Hinweis für externe Erreichbarkeit */}
            <div className="p-4 bg-amber-900/20 rounded border border-amber-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-[#FF8C00] mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-[#FFD700] mb-2">Hinweis zur Erreichbarkeit</h4>
                  <p className="text-sm text-zinc-400">
                    Wenn Ihr Home Assistant nur lokal erreichbar ist (private IP, CGNAT, DS-Lite),
                    kann die Integration nicht auf Ihren HA zugreifen. In diesem Fall:
                  </p>
                  <ul className="list-disc list-inside text-sm text-zinc-400 mt-2 space-y-1">
                    <li><strong>Nabu Casa:</strong> Einfachste Lösung für Remote-Zugriff</li>
                    <li><strong>Cloudflare Tunnel:</strong> Kostenlos, aber komplexer</li>
                    <li><strong>Tailscale/ZeroTier:</strong> VPN-Lösungen</li>
                  </ul>
                  <p className="text-sm text-zinc-500 mt-2">
                    Da die Integration auf Ihrem HA läuft und den Einsatz-Monitor abfragt,
                    muss nur der <strong>Einsatz-Monitor</strong> erreichbar sein - nicht Ihr HA!
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

// Charts Panel Component
function ChartsPanel({ authHeader, stats }) {
  const [monthlyData, setMonthlyData] = useState([]);
  const [keywordData, setKeywordData] = useState([]);
  const [yearlyData, setYearlyData] = useState([]);
  const [chartYear, setChartYear] = useState(new Date().getFullYear());
  
  const COLORS_CHART = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6'];
  
  // Calculate available years once
  const availableYears = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear - 3; y <= currentYear; y++) {
    availableYears.push(y);
  }

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const [monthlyRes, keywordRes, yearlyRes] = await Promise.all([
          axios.get(`${API}/api/einsaetze/stats/monthly?year=${chartYear}`, { headers: authHeader() }),
          axios.get(`${API}/api/einsaetze/stats/by-keyword?year=${chartYear}`, { headers: authHeader() }),
          axios.get(`${API}/api/einsaetze/stats/yearly-comparison`, { headers: authHeader() })
        ]);
        
        setMonthlyData(monthlyRes.data.data);
        setKeywordData(keywordRes.data.data);
        setYearlyData(yearlyRes.data.data);
      } catch (e) {
        console.error('Chart data fetch error:', e);
      }
    };
    
    fetchChartData();
  }, [chartYear, authHeader]);

  return (
    <div className="space-y-6">
      {/* Year Selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium">Statistiken {chartYear}</h2>
        <Select value={chartYear.toString()} onValueChange={(v) => setChartYear(parseInt(v))}>
          <SelectTrigger className="w-24 bg-[#1F2937] border-[#1F2937]/80">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#1F2937]/80 border-[#1F2937]/80">
            {availableYears.map((y) => (
              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Monthly Chart */}
      <Card className="border-[#1F2937] bg-black/40">
        <CardHeader>
          <CardTitle className="text-sm uppercase tracking-wider text-zinc-400">
            Einsätze pro Monat
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="month" stroke="#666" fontSize={12} />
                <YAxis stroke="#666" fontSize={12} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#18181b', border: '1px solid #333', borderRadius: '4px' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Bar dataKey="einsaetze" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Keyword Distribution */}
        <Card className="border-[#1F2937] bg-black/40">
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-wider text-zinc-400">
              Einsatzarten
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {keywordData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={keywordData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {keywordData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS_CHART[index % COLORS_CHART.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#18181b', border: '1px solid #333' }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-zinc-500 text-center py-8">Keine Daten</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Yearly Comparison */}
        <Card className="border-[#1F2937] bg-black/40">
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-wider text-zinc-400">
              Jahresvergleich
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={yearlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="year" stroke="#666" fontSize={12} />
                  <YAxis stroke="#666" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#18181b', border: '1px solid #333', borderRadius: '4px' }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Bar dataKey="einsaetze" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary Stats */}
      {stats && (
        <Card className="border-[#1F2937] bg-black/40">
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-wider text-zinc-400">
              Zusammenfassung {stats.year}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-[#1F2937]/80/50 rounded text-center">
                <p className="text-2xl font-bold text-[#FF8C00]">{stats.total}</p>
                <p className="text-xs text-zinc-500">Einsätze gesamt</p>
              </div>
              <div className="p-4 bg-[#1F2937]/80/50 rounded text-center">
                <p className="text-2xl font-bold text-emerald-500">{stats.active_count}</p>
                <p className="text-xs text-zinc-500">Aktive Alarme</p>
              </div>
              <div className="p-4 bg-[#1F2937]/80/50 rounded text-center">
                <p className="text-2xl font-bold text-[#FF8C00]">
                  {stats.total > 0 ? (stats.total / 12).toFixed(1) : 0}
                </p>
                <p className="text-xs text-zinc-500">Ø pro Monat</p>
              </div>
              <div className="p-4 bg-[#1F2937]/80/50 rounded text-center">
                <p className="text-2xl font-bold text-zinc-400">{stats.total_all}</p>
                <p className="text-xs text-zinc-500">Alle Jahre</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Users Panel Component
function UsersPanel({ currentUser, authHeader, tenant }) {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEmailInviteDialog, setShowEmailInviteDialog] = useState(false);
  const [emailSettings, setEmailSettings] = useState(null);
  
  // Add user form
  const [addUsername, setAddUsername] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addDisplayName, setAddDisplayName] = useState('');
  const [addRole, setAddRole] = useState('member');
  const [addSendEmail, setAddSendEmail] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  
  // Email invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  
  const isAdmin = currentUser?.role === 'super_admin';

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/users`, { headers: authHeader() });
      setUsers(res.data);
    } catch (e) { console.error(e); }
  }, [authHeader]);

  // Fetch invitations
  const fetchInvitations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/users/invitations`, { headers: authHeader() });
      setInvitations(res.data);
    } catch (e) { console.error(e); }
  }, [authHeader]);

  // Fetch email settings
  const fetchEmailSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/settings/email`, { headers: authHeader() });
      setEmailSettings(res.data);
    } catch (e) { console.error(e); }
  }, [authHeader]);

  useEffect(() => {
    fetchUsers();
    fetchInvitations();
    fetchEmailSettings();
  }, [fetchUsers, fetchInvitations, fetchEmailSettings]);

  // Add user manually
  const addUserManual = async () => {
    if (!addUsername.trim()) {
      toast.error('Benutzername ist erforderlich');
      return;
    }
    
    try {
      const res = await axios.post(`${API}/api/users/add`, {
        username: addUsername,
        email: addEmail || null,
        display_name: addDisplayName || addUsername,
        role: addRole,
        send_email: addSendEmail && addEmail
      }, { headers: authHeader() });
      
      setTempPassword(res.data.temp_password);
      if (res.data.email_sent) {
        toast.success('Benutzer erstellt und E-Mail gesendet');
      } else {
        toast.success('Benutzer erstellt');
      }
      fetchUsers();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 
                       (Array.isArray(detail) ? detail[0]?.msg : 'Fehler beim Erstellen');
      toast.error(errorMsg || 'Fehler beim Erstellen');
    }
  };

  // Invite by email
  const inviteByEmail = async () => {
    if (!inviteEmail.trim()) {
      toast.error('E-Mail ist erforderlich');
      return;
    }
    
    if (!emailSettings?.smtp_configured) {
      toast.error('E-Mail ist nicht konfiguriert. Bitte zuerst unter Einstellungen konfigurieren.');
      return;
    }
    
    try {
      await axios.post(`${API}/api/users/invite-email`, {
        email: inviteEmail,
        role: inviteRole
      }, { headers: authHeader() });
      
      toast.success(`Einladung wurde an ${inviteEmail} gesendet`);
      setShowEmailInviteDialog(false);
      setInviteEmail('');
      setInviteRole('member');
      fetchInvitations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Senden');
    }
  };

  // Cancel invitation
  const cancelInvitation = async (invId) => {
    if (!window.confirm('Einladung wirklich zurückziehen?')) return;
    
    try {
      await axios.delete(`${API}/api/users/invitations/${invId}`, { headers: authHeader() });
      toast.success('Einladung zurückgezogen');
      fetchInvitations();
    } catch (e) {
      toast.error('Fehler');
    }
  };

  // Reset password
  const resetPassword = async (userId) => {
    if (!window.confirm('Passwort wirklich zurücksetzen?')) return;
    
    try {
      const res = await axios.post(`${API}/api/users/${userId}/reset-password`, {}, { headers: authHeader() });
      toast.success(`Neues Passwort: ${res.data.temp_password}`);
      alert(`Neues temporäres Passwort: ${res.data.temp_password}\n\nBitte teilen Sie dies dem Benutzer mit.`);
    } catch (e) {
      toast.error('Fehler beim Zurücksetzen');
    }
  };

  // Remove user
  const removeUser = async (userId, username) => {
    if (!window.confirm(`Benutzer "${username}" wirklich entfernen?`)) return;
    
    try {
      await axios.delete(`${API}/api/users/${userId}`, { headers: authHeader() });
      toast.success('Benutzer entfernt');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Entfernen');
    }
  };

  // Close add dialog
  const closeAddDialog = () => {
    setShowAddDialog(false);
    setAddUsername('');
    setAddEmail('');
    setAddDisplayName('');
    setAddRole('member');
    setAddSendEmail(false);
    setTempPassword('');
  };

  return (
    <div className="space-y-6">
      {/* Users Card */}
      <Card className="border-[#1F2937] bg-black/40">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm uppercase tracking-wider text-zinc-400">
            <Users className="inline h-4 w-4 mr-2" />
            Benutzer ({users.length})
          </CardTitle>
          {isAdmin && (
            <div className="flex gap-2">
              <Button 
                size="sm" 
                variant="outline"
                className="border-[#1F2937]/80"
                onClick={() => setShowAddDialog(true)}
              >
                <Plus className="h-4 w-4 mr-2" />Manuell hinzufügen
              </Button>
              <Button 
                size="sm" 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => setShowEmailInviteDialog(true)}
                disabled={!emailSettings?.smtp_configured}
                title={!emailSettings?.smtp_configured ? 'E-Mail nicht konfiguriert' : ''}
              >
                <Mail className="h-4 w-4 mr-2" />Per E-Mail einladen
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {!emailSettings?.smtp_configured && isAdmin && (
            <div className="mb-4 p-3 bg-amber-900/20 border border-amber-700 rounded text-sm text-[#FFD700]">
              <AlertTriangle className="inline h-4 w-4 mr-2" />
              E-Mail ist nicht konfiguriert. Gehe zu <strong>Einstellungen → E-Mail</strong> um SMTP zu konfigurieren.
            </div>
          )}
          
          {users.length === 0 ? (
            <p className="text-zinc-500 text-center py-8">Keine Benutzer</p>
          ) : (
            <div className="space-y-2">
              {users.map((u) => (
                <div key={u.id} className="flex items-center justify-between p-4 bg-[#1F2937]/80/50 rounded border border-[#1F2937]">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center text-white font-bold">
                      {(u.display_name || u.username).charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium">{u.display_name || u.username}</p>
                      <p className="text-sm text-zinc-500">@{u.username} {u.email && <span className="text-zinc-600">• {u.email}</span>}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge 
                      variant="outline" 
                      className={u.role === 'tenant_admin' ? 'border-amber-600 text-[#FF8C00]' : 'border-zinc-600'}
                    >
                      {u.role === 'tenant_admin' ? 'Admin' : 'Mitglied'}
                    </Badge>
                    <Badge 
                      variant="outline" 
                      className={u.status === 'active' ? 'border-emerald-600 text-emerald-500' : 'border-[#DC2626] text-[#DC2626]'}
                    >
                      {u.status === 'active' ? 'Aktiv' : u.status}
                    </Badge>
                    {isAdmin && u.id !== currentUser.id && (
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => resetPassword(u.id)}
                          title="Passwort zurücksetzen"
                        >
                          <Key className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeUser(u.id, u.username)}
                          className="text-[#DC2626] hover:text-red-300"
                          title="Benutzer entfernen"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      {isAdmin && invitations.length > 0 && (
        <Card className="border-[#1F2937] bg-black/40">
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-wider text-zinc-400">
              <Mail className="inline h-4 w-4 mr-2" />
              Ausstehende Einladungen ({invitations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {invitations.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between p-3 bg-[#1F2937]/80/50 rounded border border-[#1F2937]">
                  <div>
                    <p className="font-medium">{inv.email}</p>
                    <p className="text-xs text-zinc-500">
                      Gültig bis: {new Date(inv.expires_at).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="border-blue-600 text-[#FF8C00]">
                      Ausstehend
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => cancelInvitation(inv.id)}
                      className="text-[#DC2626] hover:text-red-300"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add User Dialog */}
      <Dialog open={showAddDialog} onOpenChange={closeAddDialog}>
        <DialogContent className="bg-[#1F2937]/80 border-[#1F2937]">
          <DialogHeader>
            <DialogTitle>Benutzer manuell hinzufügen</DialogTitle>
          </DialogHeader>
          
          {tempPassword ? (
            <div className="space-y-4">
              <div className="p-4 bg-emerald-900/30 border border-emerald-700 rounded">
                <p className="text-emerald-400 font-medium mb-2">Benutzer erfolgreich erstellt!</p>
                <p className="text-sm text-zinc-300">Temporäres Passwort:</p>
                <div className="flex items-center gap-2 mt-1">
                  <code className="bg-[#1F2937] px-3 py-2 rounded text-[#FFD700] font-mono flex-1">
                    {tempPassword}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {navigator.clipboard.writeText(tempPassword); toast.success('Kopiert!');}}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  Bitte teilen Sie diese Zugangsdaten dem Benutzer mit.
                </p>
              </div>
              <Button onClick={closeAddDialog} className="w-full">Schließen</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>Benutzername *</Label>
                <Input
                  value={addUsername}
                  onChange={(e) => setAddUsername(e.target.value)}
                  placeholder="benutzername"
                  className="bg-[#1F2937] border-[#1F2937]/80"
                />
              </div>
              <div>
                <Label>E-Mail (optional)</Label>
                <Input
                  type="email"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  placeholder="email@beispiel.de"
                  className="bg-[#1F2937] border-[#1F2937]/80"
                />
              </div>
              <div>
                <Label>Anzeigename</Label>
                <Input
                  value={addDisplayName}
                  onChange={(e) => setAddDisplayName(e.target.value)}
                  placeholder="Max Mustermann"
                  className="bg-[#1F2937] border-[#1F2937]/80"
                />
              </div>
              <div>
                <Label>Rolle</Label>
                <Select value={addRole} onValueChange={setAddRole}>
                  <SelectTrigger className="bg-[#1F2937] border-[#1F2937]/80">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1F2937] border-[#1F2937]/80">
                    <SelectItem value="member">Mitglied</SelectItem>
                    <SelectItem value="tenant_admin">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {addEmail && emailSettings?.smtp_configured && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="sendEmail"
                    checked={addSendEmail}
                    onChange={(e) => setAddSendEmail(e.target.checked)}
                    className="rounded"
                  />
                  <label htmlFor="sendEmail" className="text-sm text-zinc-400">
                    Zugangsdaten per E-Mail senden
                  </label>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={closeAddDialog} className="flex-1 border-[#1F2937]/80">
                  Abbrechen
                </Button>
                <Button onClick={addUserManual} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  Erstellen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Invite Dialog */}
      <Dialog open={showEmailInviteDialog} onOpenChange={setShowEmailInviteDialog}>
        <DialogContent className="bg-[#1F2937]/80 border-[#1F2937]">
          <DialogHeader>
            <DialogTitle>Per E-Mail einladen</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-[#DC2626]/10 border border-blue-700 rounded text-sm text-[#FFD700]">
              <Mail className="inline h-4 w-4 mr-2" />
              Eine Einladungs-E-Mail wird gesendet. Der Empfänger kann sich selbst registrieren.
            </div>
            <div>
              <Label>E-Mail-Adresse *</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="email@beispiel.de"
                className="bg-[#1F2937] border-[#1F2937]/80"
              />
            </div>
            <div>
              <Label>Rolle</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="bg-[#1F2937] border-[#1F2937]/80">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1F2937] border-[#1F2937]/80">
                  <SelectItem value="member">Mitglied</SelectItem>
                  <SelectItem value="tenant_admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowEmailInviteDialog(false)} className="flex-1 border-[#1F2937]/80">
                Abbrechen
              </Button>
              <Button onClick={inviteByEmail} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                <Mail className="h-4 w-4 mr-2" />
                Einladung senden
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Email Settings Panel (Super Admin only)
function EmailSettingsPanel({ authHeader }) {
  const [emailSettings, setEmailSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_sender: '',
    smtp_use_tls: true,
    email_invite_subject: 'Einladung zu FireCall Tracker',
    email_invite_text: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);

  // Fetch email settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/api/settings/email`, { headers: authHeader() });
        setEmailSettings(prev => ({ ...prev, ...res.data, smtp_password: '' }));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [authHeader]);

  // Save settings
  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/settings/email`, emailSettings, { headers: authHeader() });
      toast.success('E-Mail-Einstellungen gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  // Send test email
  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Bitte Test-E-Mail-Adresse eingeben');
      return;
    }
    setTesting(true);
    try {
      await axios.post(`${API}/api/settings/email/test`, { to_email: testEmail }, { headers: authHeader() });
      toast.success(`Test-E-Mail wurde an ${testEmail} gesendet`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-zinc-500">Laden...</div>;
  }

  return (
    <div className="space-y-6">
      {/* SMTP Configuration */}
      <Card className="border-[#1F2937] bg-black/40">
        <CardHeader>
          <CardTitle className="text-sm uppercase tracking-wider text-zinc-400">
            <Mail className="inline h-4 w-4 mr-2" />
            SMTP-Konfiguration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>SMTP-Server *</Label>
              <Input
                value={emailSettings.smtp_host}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
                placeholder="smtp.beispiel.de"
                className="bg-[#1F2937] border-[#1F2937]/80"
              />
            </div>
            <div>
              <Label>Port</Label>
              <Select 
                value={String(emailSettings.smtp_port)} 
                onValueChange={(v) => setEmailSettings(prev => ({ ...prev, smtp_port: parseInt(v) }))}
              >
                <SelectTrigger className="bg-[#1F2937] border-[#1F2937]/80">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1F2937] border-[#1F2937]/80">
                  <SelectItem value="587">587 (STARTTLS)</SelectItem>
                  <SelectItem value="465">465 (SSL)</SelectItem>
                  <SelectItem value="25">25 (Unverschlüsselt)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Benutzername *</Label>
              <Input
                value={emailSettings.smtp_user}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_user: e.target.value }))}
                placeholder="noreply@beispiel.de"
                className="bg-[#1F2937] border-[#1F2937]/80"
              />
            </div>
            <div>
              <Label>Passwort *</Label>
              <Input
                type="password"
                value={emailSettings.smtp_password}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
                placeholder="••••••••"
                className="bg-[#1F2937] border-[#1F2937]/80"
              />
              <p className="text-xs text-zinc-500 mt-1">Leer lassen, um bestehendes Passwort zu behalten</p>
            </div>
          </div>
          
          <div>
            <Label>Absender-Adresse</Label>
            <Input
              value={emailSettings.smtp_sender}
              onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_sender: e.target.value }))}
              placeholder="FireCall Tracker <noreply@beispiel.de>"
              className="bg-[#1F2937] border-[#1F2937]/80"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="useTls"
              checked={emailSettings.smtp_use_tls}
              onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_use_tls: e.target.checked }))}
              className="rounded"
            />
            <label htmlFor="useTls" className="text-sm text-zinc-400">TLS/SSL verwenden</label>
          </div>
          
          <Button onClick={saveSettings} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? 'Speichern...' : 'Einstellungen speichern'}
          </Button>
        </CardContent>
      </Card>

      {/* Test Email */}
      <Card className="border-[#1F2937] bg-black/40">
        <CardHeader>
          <CardTitle className="text-sm uppercase tracking-wider text-zinc-400">
            Test-E-Mail senden
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="test@beispiel.de"
              className="bg-[#1F2937] border-[#1F2937]/80 flex-1"
            />
            <Button onClick={sendTestEmail} disabled={testing} variant="outline" className="border-[#1F2937]/80">
              {testing ? 'Senden...' : 'Test senden'}
            </Button>
          </div>
          <p className="text-xs text-zinc-500">
            Sendet eine Test-E-Mail, um die Konfiguration zu überprüfen.
          </p>
        </CardContent>
      </Card>

      {/* Invitation Email Template */}
      <Card className="border-[#1F2937] bg-black/40">
        <CardHeader>
          <CardTitle className="text-sm uppercase tracking-wider text-zinc-400">
            Einladungs-E-Mail anpassen
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Betreff</Label>
            <Input
              value={emailSettings.email_invite_subject}
              onChange={(e) => setEmailSettings(prev => ({ ...prev, email_invite_subject: e.target.value }))}
              placeholder="Einladung zu FireCall Tracker"
              className="bg-[#1F2937] border-[#1F2937]/80"
            />
          </div>
          <div>
            <Label>Eigener Text (optional)</Label>
            <textarea
              value={emailSettings.email_invite_text || ''}
              onChange={(e) => setEmailSettings(prev => ({ ...prev, email_invite_text: e.target.value }))}
              placeholder="Leer lassen für Standard-Text. Sie können hier HTML verwenden."
              className="w-full h-32 bg-[#1F2937] border border-[#1F2937]/80 rounded p-3 text-white resize-none"
            />
            <p className="text-xs text-zinc-500 mt-1">
              Leer lassen für den Standard-Einladungstext. HTML ist erlaubt.
            </p>
          </div>
          <Button onClick={saveSettings} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? 'Speichern...' : 'Template speichern'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// Admin Panel Component (Super Admin only) - Tenants, Users, Email
function AdminPanel({ authHeader, allTenants, fetchAdminData, pendingUsersCount }) {
  const [adminTab, setAdminTab] = useState('tenants');
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Tenant form
  const [showTenantDialog, setShowTenantDialog] = useState(false);
  const [newTenantName, setNewTenantName] = useState('');
  const [newTenantType, setNewTenantType] = useState('feuerwehr');
  const [newTenantDesc, setNewTenantDesc] = useState('');

  // User form
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserDisplayName, setNewUserDisplayName] = useState('');
  const [newUserRole, setNewUserRole] = useState('member');
  const [selectedUserTenants, setSelectedUserTenants] = useState([]);
  const [createdUserPassword, setCreatedUserPassword] = useState('');
  const [sendEmailToUser, setSendEmailToUser] = useState(true);
  const [emailWasSent, setEmailWasSent] = useState(false);
  const [sentToEmail, setSentToEmail] = useState('');

  // Test Alarm Dialog
  const [testAlarmDialogOpen, setTestAlarmDialogOpen] = useState(false);
  const [testAlarmUserId, setTestAlarmUserId] = useState(null);
  const [testAlarmUsername, setTestAlarmUsername] = useState('');
  const [testAlarmKeyword, setTestAlarmKeyword] = useState('Testalarm');
  const [testAlarmUnit, setTestAlarmUnit] = useState('');

  // AAO Management
  const [aaoEntries, setAaoEntries] = useState([]);
  const [selectedTenantForAAO, setSelectedTenantForAAO] = useState('');
  const [newAAOKeyword, setNewAAOKeyword] = useState('');
  const [newAAOUnit, setNewAAOUnit] = useState('');
  const [newAAOVehicles, setNewAAOVehicles] = useState('');
  const [aaoTenantSettings, setAaoTenantSettings] = useState(null);
  // AAO Dialog State
  const [aaoDialogOpen, setAaoDialogOpen] = useState(false);
  const [aaoDialogKeyword, setAaoDialogKeyword] = useState(null);
  const [aaoSelectedVehicles, setAaoSelectedVehicles] = useState([]);
  const [aaoEditingEntryId, setAaoEditingEntryId] = useState(null); // null = create new, otherwise = edit existing

  // Tenant Settings Management
  const [selectedTenantForSettings, setSelectedTenantForSettings] = useState('');
  const [tenantSettings, setTenantSettings] = useState(null);
  const [newTenantFunction, setNewTenantFunction] = useState('');
  const [newTenantVehicle, setNewTenantVehicle] = useState('');

  const ORG_TYPES = {
    feuerwehr: { label: "Feuerwehr", color: "bg-[#DC2626]" },
    thw: { label: "THW", color: "bg-[#DC2626]" },
    asb: { label: "ASB", color: "bg-orange-600" },
    drk: { label: "DRK", color: "bg-red-700" },
    dlrg: { label: "DLRG", color: "bg-yellow-600" },
    sonstige: { label: "Sonstige", color: "bg-zinc-600" }
  };

  // Fetch tenants
  const fetchTenants = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/tenants`, { headers: authHeader() });
      setTenants(res.data.data || []);
    } catch (e) {
      console.error(e);
    }
  }, [authHeader]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/users`, { headers: authHeader() });
      setUsers(res.data.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  // Fetch AAO entries for a tenant
  const fetchAAO = useCallback(async (tenantId) => {
    if (!tenantId) {
      setAaoEntries([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/api/admin/tenants/${tenantId}/aao`, { headers: authHeader() });
      setAaoEntries(res.data.data || []);
    } catch (e) {
      console.error(e);
      setAaoEntries([]);
    }
  }, [authHeader]);

  useEffect(() => {
    fetchTenants();
    fetchUsers();
  }, [fetchTenants, fetchUsers]);

  // Auto-select first tenant for AAO when tenants are loaded
  useEffect(() => {
    if (tenants.length > 0 && !selectedTenantForAAO) {
      const activeTenant = tenants.find(t => t.status !== 'deleted');
      if (activeTenant) {
        setSelectedTenantForAAO(activeTenant.id);
      }
    }
  }, [tenants, selectedTenantForAAO]);

  // Fetch AAO when tenant is selected
  useEffect(() => {
    if (selectedTenantForAAO) {
      fetchAAO(selectedTenantForAAO);
      // Also fetch tenant settings for vehicles
      axios.get(`${API}/api/admin/tenants/${selectedTenantForAAO}/settings`, { headers: authHeader() })
        .then(res => setAaoTenantSettings(res.data.data))
        .catch(() => setAaoTenantSettings(null));
    } else {
      setAaoTenantSettings(null);
    }
  }, [selectedTenantForAAO, fetchAAO, authHeader]);

  // Fetch Tenant Settings
  const fetchTenantSettings = useCallback(async (tenantId) => {
    try {
      const res = await axios.get(`${API}/api/admin/tenants/${tenantId}/settings`, { headers: authHeader() });
      setTenantSettings(res.data.data);
    } catch (e) {
      console.error('Error fetching tenant settings:', e);
      setTenantSettings(null);
    }
  }, [authHeader]);

  // Fetch settings when tenant is selected
  useEffect(() => {
    if (selectedTenantForSettings) {
      fetchTenantSettings(selectedTenantForSettings);
    } else {
      setTenantSettings(null);
    }
  }, [selectedTenantForSettings, fetchTenantSettings]);

  // Add function to tenant
  const addTenantFunction = async () => {
    if (!newTenantFunction.trim() || !selectedTenantForSettings) return;
    const functions = [...(tenantSettings?.available_functions || []), newTenantFunction.trim()];
    try {
      await axios.put(`${API}/api/admin/tenants/${selectedTenantForSettings}/settings`, 
        { available_functions: functions }, { headers: authHeader() });
      toast.success('Funktion hinzugefügt');
      setNewTenantFunction('');
      fetchTenantSettings(selectedTenantForSettings);
    } catch (e) { toast.error('Fehler beim Hinzufügen'); }
  };

  // Remove function from tenant
  const removeTenantFunction = async (func) => {
    const functions = (tenantSettings?.available_functions || []).filter(f => f !== func);
    try {
      await axios.put(`${API}/api/admin/tenants/${selectedTenantForSettings}/settings`, 
        { available_functions: functions }, { headers: authHeader() });
      toast.success('Funktion entfernt');
      fetchTenantSettings(selectedTenantForSettings);
    } catch (e) { toast.error('Fehler beim Entfernen'); }
  };

  // Add vehicle to tenant
  const addTenantVehicle = async () => {
    if (!newTenantVehicle.trim() || !selectedTenantForSettings) return;
    const vehicles = [...(tenantSettings?.available_vehicles || []), newTenantVehicle.trim()];
    try {
      await axios.put(`${API}/api/admin/tenants/${selectedTenantForSettings}/settings`, 
        { available_vehicles: vehicles }, { headers: authHeader() });
      toast.success('Fahrzeug hinzugefügt');
      setNewTenantVehicle('');
      fetchTenantSettings(selectedTenantForSettings);
    } catch (e) { toast.error('Fehler beim Hinzufügen'); }
  };

  // Remove vehicle from tenant
  const removeTenantVehicle = async (veh) => {
    const vehicles = (tenantSettings?.available_vehicles || []).filter(v => v !== veh);
    try {
      await axios.put(`${API}/api/admin/tenants/${selectedTenantForSettings}/settings`, 
        { available_vehicles: vehicles }, { headers: authHeader() });
      toast.success('Fahrzeug entfernt');
      fetchTenantSettings(selectedTenantForSettings);
    } catch (e) { toast.error('Fehler beim Entfernen'); }
  };

  // Keywords from Einsätze (for AAO drag & drop)
  const [availableKeywords, setAvailableKeywords] = useState([]);
  const [draggedVehicle, setDraggedVehicle] = useState(null);

  // Fetch keywords when AAO tenant is selected (use admin endpoint for tenant-specific keywords)
  const fetchKeywords = useCallback(async (tenantId) => {
    if (!tenantId) {
      setAvailableKeywords([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/api/admin/tenants/${tenantId}/keywords`, { headers: authHeader() });
      setAvailableKeywords(res.data.keywords || []);
    } catch (e) {
      console.error('Error fetching keywords:', e);
      setAvailableKeywords([]);
    }
  }, [authHeader]);

  useEffect(() => {
    if (selectedTenantForAAO) {
      fetchKeywords(selectedTenantForAAO);
    }
  }, [selectedTenantForAAO, fetchKeywords]);

  // Get keywords without AAO
  const keywordsWithoutAAO = availableKeywords.filter(kw => 
    !aaoEntries.some(aao => aao.keyword.toLowerCase() === kw.toLowerCase())
  );

  // Handle drag start
  const handleDragStart = (vehicle) => {
    setDraggedVehicle(vehicle);
  };

  // Handle drop on keyword
  const handleDropOnKeyword = async (keyword) => {
    if (!draggedVehicle || !selectedTenantForAAO) return;
    
    try {
      await axios.post(`${API}/api/admin/tenants/${selectedTenantForAAO}/aao`, {
        keyword: keyword,
        unit: '',
        vehicles: draggedVehicle
      }, { headers: authHeader() });
      toast.success(`AAO erstellt: ${keyword} → ${draggedVehicle}`);
      fetchAAO(selectedTenantForAAO);
    } catch (e) {
      toast.error('Fehler beim Erstellen');
    }
    setDraggedVehicle(null);
  };

  // Create tenant
  const createTenant = async () => {
    if (!newTenantName.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }
    try {
      await axios.post(`${API}/api/admin/tenants`, {
        name: newTenantName,
        organization_type: newTenantType,
        description: newTenantDesc || null
      }, { headers: authHeader() });
      toast.success('Tenant erstellt');
      setShowTenantDialog(false);
      setNewTenantName('');
      setNewTenantType('feuerwehr');
      setNewTenantDesc('');
      fetchTenants();
      if (fetchAdminData) fetchAdminData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 
                       (Array.isArray(detail) ? detail[0]?.msg : 'Fehler beim Erstellen');
      toast.error(errorMsg || 'Fehler beim Erstellen');
    }
  };

  // Delete tenant
  const deleteTenant = async (tenantId, tenantName) => {
    if (!window.confirm(`Tenant "${tenantName}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/api/admin/tenants/${tenantId}`, { headers: authHeader() });
      toast.success('Tenant gelöscht');
      fetchTenants();
      if (fetchAdminData) fetchAdminData();
    } catch (e) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Cleanup orphaned tenants
  const cleanupOrphanedTenants = async () => {
    if (!window.confirm('Alle verwaisten Tenants (ohne Namen und ohne Benutzer) wirklich löschen?')) return;
    try {
      const res = await axios.post(`${API}/api/admin/tenants/cleanup-orphaned`, {}, { headers: authHeader() });
      toast.success(res.data.message || `${res.data.deleted_count} verwaiste Tenants gelöscht`);
      fetchTenants();
      if (fetchAdminData) fetchAdminData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Bereinigen');
    }
  };

  // Assign user to tenant
  const assignUserToTenant = async (userId, tenantId) => {
    try {
      await axios.post(`${API}/api/admin/tenants/${tenantId}/users`, {
        user_id: userId,
        tenant_id: tenantId,
        is_admin: false
      }, { headers: authHeader() });
      toast.success('Benutzer wurde Tenant zugeordnet');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler bei Zuordnung');
    }
  };

  // Remove user from tenant
  const removeUserFromTenant = async (userId, tenantId) => {
    if (!window.confirm('Benutzer wirklich von diesem Tenant entfernen?')) return;
    try {
      await axios.delete(`${API}/api/admin/tenants/${tenantId}/users/${userId}`, { headers: authHeader() });
      toast.success('Benutzer wurde vom Tenant entfernt');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Entfernen');
    }
  };

  // Suspend or activate user
  const toggleUserStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'pending' : 'active';
    const action = newStatus === 'active' ? 'aktivieren' : 'sperren';
    if (!window.confirm(`Benutzer wirklich ${action}?`)) return;
    try {
      await axios.put(`${API}/api/admin/users/${userId}/status?status=${newStatus}`, {}, { headers: authHeader() });
      toast.success(`Benutzer wurde ${newStatus === 'active' ? 'aktiviert' : 'gesperrt'}`);
      fetchUsers();
      fetchAdminData(); // Update pending users count
    } catch (e) {
      const detail = e.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Fehler beim Ändern des Status';
      toast.error(errorMsg);
    }
  };

  // Delete user completely
  const deleteUser = async (userId, username) => {
    if (!window.confirm(`Benutzer "${username}" wirklich DAUERHAFT löschen? Diese Aktion kann nicht rückgängig gemacht werden!`)) return;
    try {
      await axios.delete(`${API}/api/admin/users/${userId}`, { headers: authHeader() });
      toast.success('Benutzer wurde gelöscht');
      fetchUsers();
      fetchAdminData(); // Update pending users count
    } catch (e) {
      const detail = e.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Fehler beim Löschen';
      toast.error(errorMsg);
    }
  };

  // Change user role (admin)
  const changeUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/api/admin/users/${userId}/role?role=${newRole}`, {}, { headers: authHeader() });
      toast.success('Rolle wurde geändert');
      fetchUsers();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Fehler beim Ändern der Rolle';
      toast.error(errorMsg);
    }
  };

  // Change user password (admin)
  const changeUserPassword = async (userId, username, email) => {
    const newPassword = window.prompt(`Neues Passwort für "${username}" eingeben (min. 8 Zeichen):`);
    if (!newPassword) return;
    if (newPassword.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen haben');
      return;
    }
    
    const sendEmail = email ? window.confirm(`Neues Passwort per E-Mail an ${email} senden?`) : false;
    
    try {
      const res = await axios.put(`${API}/api/admin/users/${userId}/password`, {
        new_password: newPassword,
        send_email: sendEmail
      }, { headers: authHeader() });
      
      if (res.data.email_sent) {
        toast.success('Passwort geändert und E-Mail gesendet');
      } else {
        toast.success('Passwort geändert');
        if (!sendEmail && email) {
          alert(`Neues Passwort: ${newPassword}\n\nBitte teilen Sie dies dem Benutzer mit.`);
        }
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Fehler beim Ändern';
      toast.error(errorMsg);
    }
  };

  // Create new user
  const createUser = async () => {
    if (!newUsername.trim()) {
      toast.error('Benutzername ist erforderlich');
      return;
    }
    try {
      const res = await axios.post(`${API}/api/admin/users`, {
        username: newUsername,
        email: newUserEmail || null,
        display_name: newUserDisplayName || newUsername,
        role: newUserRole,
        tenant_ids: selectedUserTenants,
        send_email: !!(sendEmailToUser && newUserEmail)
      }, { headers: authHeader() });
      
      setCreatedUserPassword(res.data.temp_password);
      setEmailWasSent(res.data.email_sent || false);
      if (res.data.email_sent) {
        setSentToEmail(newUserEmail);
        toast.success('Benutzer erstellt und E-Mail gesendet');
      } else {
        toast.success('Benutzer erstellt');
      }
      fetchUsers();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 
                       (Array.isArray(detail) ? detail[0]?.msg : 'Fehler beim Erstellen');
      toast.error(errorMsg || 'Fehler beim Erstellen');
    }
  };

  // Close user dialog and reset form
  const closeUserDialog = () => {
    setShowUserDialog(false);
    setNewUsername('');
    setNewUserEmail('');
    setNewUserDisplayName('');
    setNewUserRole('member');
    setSelectedUserTenants([]);
    setCreatedUserPassword('');
    setSendEmailToUser(true);
    setEmailWasSent(false);
    setSentToEmail('');
  };

  // Create AAO entry
  const createAAOEntry = async () => {
    if (!selectedTenantForAAO || !newAAOKeyword.trim() || !newAAOVehicles.trim()) {
      toast.error('Tenant, Stichwort und Fahrzeuge sind erforderlich');
      return;
    }
    try {
      await axios.post(`${API}/api/admin/tenants/${selectedTenantForAAO}/aao`, {
        keyword: newAAOKeyword.trim(),
        unit: newAAOUnit.trim() || null,
        vehicles: newAAOVehicles.trim()
      }, { headers: authHeader() });
      toast.success('AAO-Eintrag erstellt');
      setNewAAOKeyword('');
      setNewAAOUnit('');
      setNewAAOVehicles('');
      fetchAAO(selectedTenantForAAO);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Fehler beim Erstellen';
      toast.error(errorMsg);
    }
  };

  // Delete AAO entry
  const deleteAAOEntry = async (entryId) => {
    if (!window.confirm('AAO-Eintrag wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/api/admin/tenants/${selectedTenantForAAO}/aao/${entryId}`, { headers: authHeader() });
      toast.success('AAO-Eintrag gelöscht');
      fetchAAO(selectedTenantForAAO);
    } catch (e) {
      toast.error('Fehler beim Löschen');
    }
  };

  return (
    <Card className="border-[#1F2937] bg-black/40">
      <CardContent className="p-0">
        <Tabs value={adminTab} onValueChange={setAdminTab}>
          <TabsList className="w-full grid grid-cols-6 bg-[#1F2937]/80 rounded-none border-b border-[#1F2937]">
            <TabsTrigger value="tenants" className="data-[state=active]:bg-[#1F2937]">
              <Building2 className="h-4 w-4 mr-2" />Tenants
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#1F2937] relative">
              <Users className="h-4 w-4 mr-2" />Benutzer
              {pendingUsersCount > 0 && (
                <span className="ml-2 bg-[#DC2626] text-white text-xs rounded-full px-2 py-0.5">
                  {pendingUsersCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="tenant-settings" className="data-[state=active]:bg-[#1F2937]">
              <Settings className="h-4 w-4 mr-2" />Tenant-Einst.
            </TabsTrigger>
            <TabsTrigger value="aao" className="data-[state=active]:bg-[#1F2937]">
              <Truck className="h-4 w-4 mr-2" />AAO
            </TabsTrigger>
            <TabsTrigger value="email" className="data-[state=active]:bg-[#1F2937]">
              <Mail className="h-4 w-4 mr-2" />E-Mail
            </TabsTrigger>
            <TabsTrigger value="system" className="data-[state=active]:bg-[#1F2937]">
              <Wrench className="h-4 w-4 mr-2" />System
            </TabsTrigger>
          </TabsList>

          {/* Pending Users Alert */}
          {pendingUsersCount > 0 && adminTab === 'tenants' && (
            <div className="mx-6 mt-4 p-4 bg-amber-900/30 border border-amber-700 rounded-lg flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-[#FF8C00] flex-shrink-0" />
              <div className="flex-1">
                <p className="text-amber-200 font-medium">
                  {pendingUsersCount} {pendingUsersCount === 1 ? 'Benutzer wartet' : 'Benutzer warten'} auf Freischaltung
                </p>
                <p className="text-[#FFD700]/70 text-sm">
                  Wechseln Sie zum Benutzer-Tab, um die Anfragen zu bearbeiten.
                </p>
              </div>
              <Button 
                size="sm" 
                variant="outline" 
                className="border-amber-600 text-[#FF8C00] hover:bg-amber-900/50"
                onClick={() => setAdminTab('users')}
              >
                Anzeigen
              </Button>
            </div>
          )}

          {/* Tenants Tab */}
          <TabsContent value="tenants" className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Tenants ({tenants.length})</h3>
                <div className="flex gap-2">
                  <Button onClick={cleanupOrphanedTenants} variant="outline" size="sm" className="text-[#FFD700] border-amber-600 hover:bg-amber-600/20">
                    <Trash2 className="h-4 w-4 mr-2" />Verwaiste bereinigen
                  </Button>
                  <Button onClick={() => setShowTenantDialog(true)} className="bg-emerald-600 hover:bg-emerald-700" size="sm">
                    <Plus className="h-4 w-4 mr-2" />Neuer Tenant
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                {tenants.filter(t => t.status !== 'deleted').map((tenant) => (
                  <div key={tenant.id} className="flex items-center justify-between p-4 bg-[#1F2937]/80/50 rounded border border-[#1F2937]">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${ORG_TYPES[tenant.organization_type]?.color || 'bg-zinc-700'}`}>
                        <Building2 className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium">{tenant.name}</p>
                        <p className="text-xs text-zinc-500">
                          {ORG_TYPES[tenant.organization_type]?.label || tenant.organization_type}
                          {tenant.description && ` • ${tenant.description}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={tenant.status === 'active' ? 'border-emerald-600 text-emerald-500' : 'border-zinc-600'}>
                        {tenant.status === 'active' ? 'Aktiv' : tenant.status}
                      </Badge>
                      <Button variant="ghost" size="sm" onClick={() => deleteTenant(tenant.id, tenant.name)} className="text-[#DC2626] hover:text-red-300">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {tenants.length === 0 && (
                  <p className="text-zinc-500 text-center py-8">Keine Tenants vorhanden</p>
                )}
              </div>

              {/* Create Tenant Dialog */}
              <Dialog open={showTenantDialog} onOpenChange={setShowTenantDialog}>
                <DialogContent className="bg-[#1F2937]/80 border-[#1F2937]">
                  <DialogHeader>
                    <DialogTitle>Neuer Tenant</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Name *</Label>
                      <Input
                        value={newTenantName}
                        onChange={(e) => setNewTenantName(e.target.value)}
                        placeholder="z.B. Feuerwehr Musterstadt"
                        className="bg-[#1F2937] border-[#1F2937]/80"
                      />
                    </div>
                    <div>
                      <Label>Organisationstyp</Label>
                      <Select value={newTenantType} onValueChange={setNewTenantType}>
                        <SelectTrigger className="bg-[#1F2937] border-[#1F2937]/80">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1F2937] border-[#1F2937]/80">
                          {Object.entries(ORG_TYPES).map(([key, val]) => (
                            <SelectItem key={key} value={key}>{val.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Beschreibung (optional)</Label>
                      <Input
                        value={newTenantDesc}
                        onChange={(e) => setNewTenantDesc(e.target.value)}
                        placeholder="Optional"
                        className="bg-[#1F2937] border-[#1F2937]/80"
                      />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" onClick={() => setShowTenantDialog(false)} className="flex-1 border-[#1F2937]/80">
                        Abbrechen
                      </Button>
                      <Button onClick={createTenant} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                        Erstellen
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Alle Benutzer ({users.length})</h3>
                <Button onClick={() => setShowUserDialog(true)} className="bg-emerald-600 hover:bg-emerald-700" size="sm">
                  <Plus className="h-4 w-4 mr-2" />Neuer Benutzer
                </Button>
              </div>
              <div className="space-y-2">
                {users.map((u) => (
                  <div key={u.id} className="flex items-center justify-between p-4 bg-[#1F2937]/80/50 rounded border border-[#1F2937]">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center text-white font-bold">
                        {(u.display_name || u.username).charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{u.display_name || u.username}</span>
                          {u.role === 'super_admin' && <Badge className="bg-purple-600">Super Admin</Badge>}
                          {u.role === 'tenant_admin' && <Badge className="bg-amber-600">Admin</Badge>}
                        </div>
                        <p className="text-sm text-zinc-500">@{u.username} {u.email && `• ${u.email}`}</p>
                        {u.tenants && u.tenants.length > 0 && (
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {u.tenants.map((t) => (
                              <Badge key={t.id} variant="outline" className="text-xs border-[#1F2937]/80 pr-1">
                                {t.name}
                                {u.role !== 'super_admin' && (
                                  <button
                                    onClick={() => removeUserFromTenant(u.id, t.id)}
                                    className="ml-1 text-[#DC2626] hover:text-red-300"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                )}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {u.role !== 'super_admin' && (
                        <Select value={u.role} onValueChange={(newRole) => changeUserRole(u.id, newRole)}>
                          <SelectTrigger className="w-28 bg-[#1F2937] border-[#1F2937]/80 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1F2937] border-[#1F2937]/80">
                            <SelectItem value="member">Mitglied</SelectItem>
                            <SelectItem value="tenant_admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      {u.role !== 'super_admin' && (
                        <Select onValueChange={(tenantId) => assignUserToTenant(u.id, tenantId)}>
                          <SelectTrigger className="w-40 bg-[#1F2937] border-[#1F2937]/80 text-xs">
                            <SelectValue placeholder="+ Tenant zuweisen" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1F2937] border-[#1F2937]/80">
                            {tenants
                              .filter(t => !u.tenants?.some(ut => ut.id === t.id))
                              .map((tenant) => (
                                <SelectItem key={tenant.id} value={tenant.id}>{tenant.name}</SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Badge variant="outline" className={u.status === 'active' ? 'border-emerald-600 text-emerald-500' : 'border-[#DC2626] text-[#DC2626]'}>
                        {u.status === 'active' ? 'Aktiv' : u.status === 'pending' ? 'Gesperrt' : u.status}
                      </Badge>
                      {u.role !== 'super_admin' && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => changeUserPassword(u.id, u.username, u.email)}
                            title="Passwort ändern"
                            className="text-[#FF8C00] hover:text-[#FFD700]"
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setTestAlarmUserId(u.id);
                              setTestAlarmUsername(u.username);
                              setTestAlarmKeyword('Testalarm');
                              setTestAlarmUnit('');
                              setTestAlarmDialogOpen(true);
                            }}
                            title="Testalarm senden"
                            className="text-[#FF8C00] hover:text-[#FFD700]"
                          >
                            <Bell className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleUserStatus(u.id, u.status)}
                            title={u.status === 'active' ? 'Benutzer sperren' : 'Benutzer aktivieren'}
                            className={u.status === 'active' ? 'text-[#FFD700] hover:text-amber-300' : 'text-emerald-400 hover:text-emerald-300'}
                          >
                            {u.status === 'active' ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteUser(u.id, u.username)}
                            title="Benutzer löschen"
                            className="text-[#DC2626] hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {users.length === 0 && (
                  <p className="text-zinc-500 text-center py-8">Keine Benutzer vorhanden</p>
                )}
              </div>

              {/* Create User Dialog */}
              <Dialog open={showUserDialog} onOpenChange={closeUserDialog}>
                <DialogContent className="bg-[#1F2937]/80 border-[#1F2937]">
                  <DialogHeader>
                    <DialogTitle>Neuer Benutzer</DialogTitle>
                  </DialogHeader>
                  
                  {createdUserPassword ? (
                    <div className="space-y-4">
                      <div className="p-4 bg-emerald-900/30 border border-emerald-700 rounded">
                        <p className="text-emerald-400 font-medium mb-2">Benutzer erfolgreich erstellt!</p>
                        {emailWasSent ? (
                          <div className="p-3 bg-blue-900/30 border border-blue-700 rounded mb-3">
                            <p className="text-[#FF8C00] text-sm">
                              <Mail className="inline h-4 w-4 mr-1" />
                              E-Mail mit Zugangsdaten wurde an {sentToEmail} gesendet.
                            </p>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm text-zinc-300">Temporäres Passwort:</p>
                            <div className="flex items-center gap-2 mt-1">
                              <code className="bg-[#1F2937] px-3 py-2 rounded text-[#FFD700] font-mono flex-1">
                                {createdUserPassword}
                              </code>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {navigator.clipboard.writeText(createdUserPassword); toast.success('Kopiert!');}}
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            </div>
                            <p className="text-xs text-zinc-500 mt-2">
                              Bitte teilen Sie diese Zugangsdaten dem Benutzer mit.
                            </p>
                          </>
                        )}
                      </div>
                      <Button onClick={closeUserDialog} className="w-full">Schließen</Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <Label>Benutzername *</Label>
                        <Input
                          value={newUsername}
                          onChange={(e) => setNewUsername(e.target.value)}
                          placeholder="benutzername"
                          className="bg-[#1F2937] border-[#1F2937]/80"
                        />
                      </div>
                      <div>
                        <Label>E-Mail (optional)</Label>
                        <Input
                          type="email"
                          value={newUserEmail}
                          onChange={(e) => setNewUserEmail(e.target.value)}
                          placeholder="email@beispiel.de"
                          className="bg-[#1F2937] border-[#1F2937]/80"
                        />
                      </div>
                      <div>
                        <Label>Anzeigename</Label>
                        <Input
                          value={newUserDisplayName}
                          onChange={(e) => setNewUserDisplayName(e.target.value)}
                          placeholder="Max Mustermann"
                          className="bg-[#1F2937] border-[#1F2937]/80"
                        />
                      </div>
                      <div>
                        <Label>Rolle</Label>
                        <Select value={newUserRole} onValueChange={setNewUserRole}>
                          <SelectTrigger className="bg-[#1F2937] border-[#1F2937]/80">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1F2937] border-[#1F2937]/80">
                            <SelectItem value="member">Mitglied</SelectItem>
                            <SelectItem value="tenant_admin">Administrator</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Tenants zuweisen (optional)</Label>
                        <div className="flex flex-wrap gap-2 mt-2 p-3 bg-[#1F2937] rounded border border-[#1F2937]/80 min-h-[60px]">
                          {tenants.filter(t => t.status !== 'deleted').map((tenant) => (
                            <Badge
                              key={tenant.id}
                              variant={selectedUserTenants.includes(tenant.id) ? "default" : "outline"}
                              className={`cursor-pointer ${selectedUserTenants.includes(tenant.id) ? 'bg-emerald-600' : 'border-zinc-600 hover:border-zinc-500'}`}
                              onClick={() => {
                                if (selectedUserTenants.includes(tenant.id)) {
                                  setSelectedUserTenants(selectedUserTenants.filter(id => id !== tenant.id));
                                } else {
                                  setSelectedUserTenants([...selectedUserTenants, tenant.id]);
                                }
                              }}
                            >
                              {selectedUserTenants.includes(tenant.id) && <CheckCircle2 className="h-3 w-3 mr-1" />}
                              {tenant.name}
                            </Badge>
                          ))}
                          {tenants.filter(t => t.status !== 'deleted').length === 0 && (
                            <span className="text-zinc-500 text-sm">Keine Tenants vorhanden</span>
                          )}
                        </div>
                      </div>
                      {newUserEmail && (
                        <div className="flex items-center gap-2 p-3 bg-[#1F2937]/50 rounded border border-[#1F2937]/80">
                          <input
                            type="checkbox"
                            id="sendEmailCheckbox"
                            checked={sendEmailToUser}
                            onChange={(e) => setSendEmailToUser(e.target.checked)}
                            className="rounded"
                          />
                          <label htmlFor="sendEmailCheckbox" className="text-sm text-zinc-300 flex items-center gap-2">
                            <Mail className="h-4 w-4 text-[#FF8C00]" />
                            Zugangsdaten per E-Mail senden
                          </label>
                        </div>
                      )}
                      <div className="flex gap-2 pt-2">
                        <Button variant="outline" onClick={closeUserDialog} className="flex-1 border-[#1F2937]/80">
                          Abbrechen
                        </Button>
                        <Button onClick={createUser} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                          Erstellen
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              {/* Test Alarm Dialog */}
              <Dialog open={testAlarmDialogOpen} onOpenChange={setTestAlarmDialogOpen}>
                <DialogContent className="bg-[#1F2937]/80 border-[#1F2937]/80 max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Bell className="h-5 w-5 text-[#FF8C00]" />
                      Testalarm für {testAlarmUsername}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Stichwort / Keyword</Label>
                      <Input
                        value={testAlarmKeyword}
                        onChange={(e) => setTestAlarmKeyword(e.target.value)}
                        placeholder="z.B. B1, H1, B GMA"
                        className="bg-[#1F2937] border-[#1F2937]/80"
                      />
                      <p className="text-xs text-zinc-500">
                        Geben Sie ein Stichwort ein, das in der Alarm-Benachrichtigung verwendet wird.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Einheit (optional)</Label>
                      <Input
                        value={testAlarmUnit}
                        onChange={(e) => setTestAlarmUnit(e.target.value)}
                        placeholder="z.B. Alamos GmbH"
                        className="bg-[#1F2937] border-[#1F2937]/80"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button variant="outline" onClick={() => setTestAlarmDialogOpen(false)} className="border-[#1F2937]/80">
                        Abbrechen
                      </Button>
                      <Button
                        onClick={async () => {
                          if (!testAlarmKeyword.trim()) {
                            toast.error('Bitte geben Sie ein Stichwort ein');
                            return;
                          }
                          try {
                            await axios.post(`${API}/api/admin/users/${testAlarmUserId}/test-alarm`, {
                              keyword: testAlarmKeyword,
                              unit: testAlarmUnit || 'Test-Einheit'
                            }, { headers: authHeader() });
                            toast.success(`Testalarm für ${testAlarmUsername} gesendet`);
                            setTestAlarmDialogOpen(false);
                          } catch (e) {
                            toast.error('Fehler beim Senden des Testalarms');
                          }
                        }}
                        className="bg-[#DC2626] hover:bg-[#DC2626]/90"
                      >
                        <Bell className="h-4 w-4 mr-2" />
                        Testalarm senden
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </TabsContent>

          {/* Tenant Settings Tab */}
          <TabsContent value="tenant-settings" className="p-6">
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">Tenant-Einstellungen</h3>
                <p className="text-sm text-zinc-500 mb-4">
                  Definieren Sie Funktionen und Fahrzeuge für jeden Tenant. Diese werden den Benutzern bei der Einsatz-Teilnahme angezeigt.
                </p>
              </div>

              {/* Tenant Auswahl */}
              <div className="flex items-center gap-4">
                <Label>Tenant auswählen:</Label>
                <Select value={selectedTenantForSettings || ''} onValueChange={setSelectedTenantForSettings}>
                  <SelectTrigger className="w-64 bg-[#1F2937] border-[#1F2937]/80">
                    <SelectValue placeholder="Tenant wählen..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1F2937]/80 border-[#1F2937]/80">
                    {allTenants.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedTenantForSettings && tenantSettings && (
                <div className="space-y-6 pt-4">
                  {/* Functions */}
                  <div className="p-4 bg-[#1F2937]/80/50 rounded border border-[#1F2937]">
                    <h4 className="font-medium mb-3 text-[#FF8C00]">Verfügbare Funktionen</h4>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(tenantSettings.available_functions || []).map((f, i) => (
                        <Badge key={i} variant="outline" className="border-blue-700 text-[#FF8C00] pr-1">
                          {f}
                          <button onClick={() => removeTenantFunction(f)} className="ml-2 text-[#DC2626] hover:text-red-300">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                      {(!tenantSettings.available_functions || tenantSettings.available_functions.length === 0) && (
                        <p className="text-zinc-600 text-sm">Keine Funktionen definiert</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={newTenantFunction}
                        onChange={(e) => setNewTenantFunction(e.target.value)}
                        placeholder="Neue Funktion (z.B. Maschinist)"
                        className="bg-[#1F2937] border-[#1F2937]/80 max-w-xs"
                        onKeyDown={(e) => e.key === 'Enter' && addTenantFunction()}
                      />
                      <Button onClick={addTenantFunction} size="sm" className="bg-[#DC2626] hover:bg-[#DC2626]/90">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Vehicles */}
                  <div className="p-4 bg-[#1F2937]/80/50 rounded border border-[#1F2937]">
                    <h4 className="font-medium mb-3 text-[#FFD700]">Verfügbare Fahrzeuge</h4>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(tenantSettings.available_vehicles || []).map((v, i) => (
                        <Badge key={i} variant="outline" className="border-amber-700 text-[#FFD700] pr-1">
                          {v}
                          <button onClick={() => removeTenantVehicle(v)} className="ml-2 text-[#DC2626] hover:text-red-300">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                      {(!tenantSettings.available_vehicles || tenantSettings.available_vehicles.length === 0) && (
                        <p className="text-zinc-600 text-sm">Keine Fahrzeuge definiert</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={newTenantVehicle}
                        onChange={(e) => setNewTenantVehicle(e.target.value)}
                        placeholder="Neues Fahrzeug (z.B. HLF 10)"
                        className="bg-[#1F2937] border-[#1F2937]/80 max-w-xs"
                        onKeyDown={(e) => e.key === 'Enter' && addTenantVehicle()}
                      />
                      <Button onClick={addTenantVehicle} size="sm" className="bg-amber-600 hover:bg-amber-700">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Alexa Notification Settings */}
                  <div className="p-4 bg-[#1F2937]/80/50 rounded border border-[#1F2937]">
                    <h4 className="font-medium mb-3 text-purple-400 flex items-center gap-2">
                      <Volume2 className="h-4 w-4" />
                      Alexa Sprachbenachrichtigung
                    </h4>
                    <p className="text-sm text-zinc-500 mb-4">
                      Wählen Sie, wie Alexa bei einem Alarm benachrichtigen soll.
                    </p>
                    
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm">Benachrichtigungsmethode</Label>
                        <Select 
                          value={tenantSettings.alexa_notify_method || 'none'} 
                          onValueChange={async (value) => {
                            try {
                              await axios.put(`${API}/api/admin/tenants/${selectedTenantForSettings}/settings`, {
                                alexa_notify_method: value
                              }, { headers: authHeader() });
                              fetchTenantSettings(selectedTenantForSettings);
                              toast.success('Methode gespeichert');
                            } catch (e) { toast.error('Fehler beim Speichern'); }
                          }}
                        >
                          <SelectTrigger className="w-full bg-[#1F2937] border-[#1F2937]/80 mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1F2937]/80 border-[#1F2937]/80">
                            <SelectItem value="none">Keine Alexa-Benachrichtigung</SelectItem>
                            <SelectItem value="home_assistant">Home Assistant (erfordert HA-Integration)</SelectItem>
                            <SelectItem value="notify_me">Alexa "Notify Me" Skill (direkt)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {tenantSettings.alexa_notify_method === 'notify_me' && (
                        <div className="p-3 bg-purple-900/20 rounded border border-purple-800/50 space-y-3">
                          <p className="text-sm text-purple-300">
                            <strong>Einrichtung:</strong> Installieren Sie den kostenlosen "Notify Me" Skill in der Alexa App und kopieren Sie den Access Code.
                          </p>
                          <div>
                            <Label className="text-sm">Notify Me Access Code</Label>
                            <div className="flex gap-2 mt-1">
                              <Input
                                type="password"
                                value={tenantSettings.alexa_notify_me_access_code || ''}
                                onChange={async (e) => {
                                  const code = e.target.value;
                                  try {
                                    await axios.put(`${API}/api/admin/tenants/${selectedTenantForSettings}/settings`, {
                                      alexa_notify_me_access_code: code
                                    }, { headers: authHeader() });
                                    fetchTenantSettings(selectedTenantForSettings);
                                  } catch (err) { console.error(err); }
                                }}
                                placeholder="amzn1.ask.account.xxxxx"
                                className="bg-[#1F2937] border-[#1F2937]/80"
                              />
                            </div>
                            <p className="text-xs text-zinc-500 mt-1">
                              Den Access Code finden Sie in der Alexa App unter "Notify Me" Skill Einstellungen.
                            </p>
                          </div>
                        </div>
                      )}

                      {tenantSettings.alexa_notify_method === 'home_assistant' && (
                        <div className="p-3 bg-[#DC2626]/10 rounded border border-[#DC2626]/50/50">
                          <p className="text-sm text-[#FFD700]">
                            Die Alexa-Benachrichtigung wird über Home Assistant gesteuert. 
                            Konfigurieren Sie die Alexa-Einstellungen in der Einsatz-Monitor Integration in Home Assistant.
                          </p>
                        </div>
                      )}

                      {tenantSettings.alexa_notify_method && tenantSettings.alexa_notify_method !== 'none' && (
                        <div>
                          <Label className="text-sm">Ansage-Text (optional)</Label>
                          <Input
                            value={tenantSettings.alexa_message_template || ''}
                            onChange={async (e) => {
                              const template = e.target.value;
                              try {
                                await axios.put(`${API}/api/admin/tenants/${selectedTenantForSettings}/settings`, {
                                  alexa_message_template: template
                                }, { headers: authHeader() });
                                fetchTenantSettings(selectedTenantForSettings);
                              } catch (err) { console.error(err); }
                            }}
                            placeholder="Achtung Feuerwehreinsatz! Stichwort: {keyword}. Fahrzeuge: {vehicles}"
                            className="bg-[#1F2937] border-[#1F2937]/80 mt-1"
                          />
                          <p className="text-xs text-zinc-500 mt-1">
                            Platzhalter: {'{keyword}'}, {'{unit}'}, {'{vehicles}'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!selectedTenantForSettings && (
                <div className="p-8 text-center text-zinc-500">
                  Bitte wählen Sie einen Tenant aus, um dessen Einstellungen zu bearbeiten.
                </div>
              )}
            </div>
          </TabsContent>

          {/* AAO Tab */}
          <TabsContent value="aao" className="p-6">
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">Ausrückeordnung (AAO)</h3>
                <p className="text-sm text-zinc-500 mb-4">
                  Ziehen Sie ein Fahrzeug auf ein Stichwort, um eine AAO zu erstellen.
                </p>
              </div>

              {/* Tenant Auswahl */}
              <div className="p-4 bg-[#1F2937]/80/50 rounded border border-[#1F2937]">
                <Label className="mb-2 block">Tenant auswählen</Label>
                <Select value={selectedTenantForAAO} onValueChange={setSelectedTenantForAAO}>
                  <SelectTrigger className="w-full md:w-64 bg-[#1F2937] border-[#1F2937]/80">
                    <SelectValue placeholder="Tenant wählen..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1F2937] border-[#1F2937]/80">
                    {tenants.filter(t => t.status !== 'deleted').map((tenant) => (
                      <SelectItem key={tenant.id} value={tenant.id}>{tenant.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedTenantForAAO && (
                <>
                  {/* AAO Dialog für Fahrzeugauswahl */}
                  <Dialog open={aaoDialogOpen} onOpenChange={setAaoDialogOpen}>
                    <DialogContent className="bg-[#1F2937]/80 border-[#1F2937] max-w-2xl">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <Truck className="h-5 w-5 text-[#FF8C00]" />
                          Fahrzeuge für <Badge className="bg-amber-600 font-mono ml-2">{aaoDialogKeyword}</Badge>
                        </DialogTitle>
                      </DialogHeader>
                      
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        {/* Linke Seite: Verfügbare Fahrzeuge */}
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-zinc-400">Verfügbare Fahrzeuge</h4>
                          <div className="max-h-[300px] overflow-y-auto space-y-1 p-2 bg-[#1F2937]/50 rounded border border-[#1F2937]/80">
                            {(aaoTenantSettings?.available_vehicles || []).filter(v => !aaoSelectedVehicles.includes(v)).map((vehicle) => (
                              <div
                                key={vehicle}
                                className="flex items-center justify-between p-2 rounded hover:bg-zinc-700/50 cursor-pointer"
                                onClick={() => setAaoSelectedVehicles([...aaoSelectedVehicles, vehicle])}
                              >
                                <span className="text-zinc-300 text-sm">{vehicle}</span>
                                <Plus className="h-4 w-4 text-emerald-500" />
                              </div>
                            ))}
                            {(aaoTenantSettings?.available_vehicles || []).filter(v => !aaoSelectedVehicles.includes(v)).length === 0 && (
                              <p className="text-zinc-500 text-sm text-center py-2">Alle Fahrzeuge ausgewählt</p>
                            )}
                          </div>
                        </div>

                        {/* Rechte Seite: Ausgewählte Fahrzeuge (sortierbar) */}
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-emerald-400">Ausgewählte Fahrzeuge (Reihenfolge)</h4>
                          <div className="max-h-[300px] overflow-y-auto space-y-1 p-2 bg-[#1F2937]/50 rounded border border-emerald-700/50">
                            {aaoSelectedVehicles.length === 0 ? (
                              <p className="text-zinc-500 text-sm text-center py-4">Klicken Sie links auf Fahrzeuge um sie hinzuzufügen</p>
                            ) : (
                              aaoSelectedVehicles.map((vehicle, index) => (
                                <div
                                  key={vehicle}
                                  className="flex items-center gap-2 p-2 bg-zinc-700/50 rounded border border-zinc-600"
                                >
                                  <span className="text-[#FF8C00] font-mono text-xs w-5">{index + 1}.</span>
                                  <span className="text-zinc-200 text-sm flex-1">{vehicle}</span>
                                  <div className="flex gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 w-6 p-0 text-zinc-400 hover:text-white"
                                      disabled={index === 0}
                                      onClick={() => {
                                        const newOrder = [...aaoSelectedVehicles];
                                        [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
                                        setAaoSelectedVehicles(newOrder);
                                      }}
                                    >
                                      <ChevronUp className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 w-6 p-0 text-zinc-400 hover:text-white"
                                      disabled={index === aaoSelectedVehicles.length - 1}
                                      onClick={() => {
                                        const newOrder = [...aaoSelectedVehicles];
                                        [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
                                        setAaoSelectedVehicles(newOrder);
                                      }}
                                    >
                                      <ChevronDown className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 w-6 p-0 text-[#DC2626] hover:text-red-300"
                                      onClick={() => setAaoSelectedVehicles(aaoSelectedVehicles.filter(v => v !== vehicle))}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Vorschau */}
                      {aaoSelectedVehicles.length > 0 && (
                        <div className="mt-4 p-3 bg-[#1F2937]/50 rounded border border-[#1F2937]/80">
                          <h4 className="text-xs font-medium text-zinc-400 mb-1">Vorschau:</h4>
                          <p className="text-sm text-zinc-300 font-mono">{aaoSelectedVehicles.join(', ')}</p>
                        </div>
                      )}

                      <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setAaoDialogOpen(false)}>
                          Abbrechen
                        </Button>
                        <Button
                          className="bg-emerald-600 hover:bg-emerald-700"
                          disabled={aaoSelectedVehicles.length === 0}
                          onClick={async () => {
                            try {
                              if (aaoEditingEntryId) {
                                // Update existing entry
                                await axios.put(`${API}/api/admin/tenants/${selectedTenantForAAO}/aao/${aaoEditingEntryId}`, {
                                  keyword: aaoDialogKeyword,
                                  vehicles: aaoSelectedVehicles.join(', ')
                                }, { headers: authHeader() });
                                toast.success(`AAO aktualisiert: ${aaoDialogKeyword}`);
                              } else {
                                // Create new entry
                                await axios.post(`${API}/api/admin/tenants/${selectedTenantForAAO}/aao`, {
                                  keyword: aaoDialogKeyword,
                                  vehicles: aaoSelectedVehicles.join(', ')
                                }, { headers: authHeader() });
                                toast.success(`AAO erstellt: ${aaoDialogKeyword}`);
                              }
                              fetchAAO(selectedTenantForAAO);
                              setAaoDialogOpen(false);
                              setAaoSelectedVehicles([]);
                              setAaoEditingEntryId(null);
                            } catch (err) {
                              toast.error(aaoEditingEntryId ? "Fehler beim Aktualisieren" : "Fehler beim Erstellen");
                            }
                          }}
                        >
                          <Save className="h-4 w-4 mr-2" /> Speichern
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Stichwörter ohne AAO */}
                  <div className="p-4 bg-[#1F2937]/80/50 rounded border border-red-800/50">
                    <h4 className="text-sm font-medium text-[#DC2626] mb-3 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Stichwörter ohne AAO ({keywordsWithoutAAO.length})
                    </h4>
                    {keywordsWithoutAAO.length === 0 ? (
                      <p className="text-zinc-500 text-sm py-2 text-center">
                        Alle Stichwörter haben Fahrzeuge zugewiesen ✓
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {keywordsWithoutAAO.map((kw) => (
                          <Button
                            key={kw}
                            variant="outline"
                            className="border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626]/20"
                            onClick={() => {
                              setAaoEditingEntryId(null);
                              setAaoDialogKeyword(kw);
                              setAaoSelectedVehicles([]);
                              setAaoDialogOpen(true);
                            }}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            {kw}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Stichwörter mit AAO */}
                  <div className="p-4 bg-[#1F2937]/80/50 rounded border border-emerald-800/50">
                    <h4 className="text-sm font-medium text-emerald-400 mb-3 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      Stichwörter mit Fahrzeugen ({aaoEntries.length})
                    </h4>
                    {aaoEntries.length === 0 ? (
                      <p className="text-zinc-500 text-center py-4">Keine AAO-Einträge für diesen Tenant</p>
                    ) : (
                      <div className="space-y-2">
                        {aaoEntries.map((entry) => (
                          <div key={entry.id} className="flex items-center justify-between p-3 bg-[#1F2937]/50 rounded border border-[#1F2937]/80">
                            <div className="flex items-center gap-4">
                              <Badge className="bg-amber-600 font-mono">{entry.keyword}</Badge>
                              {entry.unit && (
                                <Badge variant="outline" className="border-blue-600 text-[#FF8C00] font-mono">
                                  {entry.unit}
                                </Badge>
                              )}
                              <span className="text-zinc-400">→</span>
                              <span className="text-zinc-300 font-mono text-sm">{entry.vehicles}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setAaoEditingEntryId(entry.id);
                                  setAaoDialogKeyword(entry.keyword);
                                  setAaoSelectedVehicles(entry.vehicles ? entry.vehicles.split(',').map(v => v.trim()).filter(v => v) : []);
                                  setAaoDialogOpen(true);
                                }}
                                className="text-zinc-400 hover:text-white"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteAAOEntry(entry.id)}
                                className="text-[#DC2626] hover:text-red-300"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Manueller Eintrag */}
                  <div className="p-4 bg-[#1F2937]/80/50 rounded border border-[#1F2937] space-y-3">
                    <p className="text-sm font-medium text-[#FF8C00] flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      Manueller Eintrag (mit Einheit)
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs text-zinc-500">Stichwort *</Label>
                        <Input
                          value={newAAOKeyword}
                          onChange={(e) => setNewAAOKeyword(e.target.value)}
                          placeholder="z.B. H1, B1"
                          className="bg-[#1F2937] border-[#1F2937]/80"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">Einheit (optional)</Label>
                        <Input
                          value={newAAOUnit}
                          onChange={(e) => setNewAAOUnit(e.target.value)}
                          placeholder="z.B. 11-83-1"
                          className="bg-[#1F2937] border-[#1F2937]/80"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">Fahrzeuge *</Label>
                        <Input
                          value={newAAOVehicles}
                          onChange={(e) => setNewAAOVehicles(e.target.value)}
                          placeholder="z.B. LF 16/12, DLK"
                          className="bg-[#1F2937] border-[#1F2937]/80"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button onClick={createAAOEntry} size="sm" className="bg-emerald-600 hover:bg-emerald-700 w-full">
                          <Plus className="h-4 w-4 mr-2" />Hinzufügen
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          {/* Email Tab */}
          <TabsContent value="email" className="p-6">
            <EmailSettingsPanel authHeader={authHeader} />
          </TabsContent>
          
          {/* System / Maintenance Tab */}
          <TabsContent value="system" className="p-6">
            <MaintenancePanel authHeader={authHeader} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

// Maintenance Panel Component (SuperAdmin only)
function MaintenancePanel({ authHeader }) {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/api/admin/settings/maintenance`, { headers: authHeader() });
        setMaintenanceMode(res.data.maintenance_mode || false);
        setMaintenanceMessage(res.data.maintenance_message || '');
      } catch (e) {
        console.error('Failed to fetch maintenance settings:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [authHeader]);

  const toggleMaintenanceMode = async (enabled) => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/settings/maintenance`, {
        maintenance_mode: enabled,
        maintenance_message: maintenanceMessage
      }, { headers: authHeader() });
      
      setMaintenanceMode(enabled);
      toast.success(enabled ? 'Wartungsmodus aktiviert' : 'Wartungsmodus deaktiviert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Ändern');
    } finally {
      setSaving(false);
    }
  };

  const saveMessage = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/settings/maintenance`, {
        maintenance_mode: maintenanceMode,
        maintenance_message: maintenanceMessage
      }, { headers: authHeader() });
      
      toast.success('Nachricht gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-zinc-500">Laden...</div>;
  }

  return (
    <div className="space-y-6">
      <Card className={`border-[#1F2937] ${maintenanceMode ? 'bg-amber-900/20 border-amber-700' : 'bg-black/40'}`}>
        <CardHeader>
          <CardTitle className="text-sm uppercase tracking-wider text-zinc-400 flex items-center gap-2">
            <Wrench className="h-4 w-4" />
            Wartungsmodus
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status Banner */}
          {maintenanceMode && (
            <div className="p-4 bg-amber-900/40 border border-amber-600 rounded-lg flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-amber-400" />
              <div>
                <p className="font-medium text-amber-300">Wartungsmodus ist aktiv</p>
                <p className="text-sm text-amber-400/80">
                  Nur Super-Admins können sich anmelden. Registrierungen sind deaktiviert.
                </p>
              </div>
            </div>
          )}
          
          {/* Toggle */}
          <div className="flex items-center justify-between p-4 bg-[#1F2937]/50 rounded-lg border border-[#1F2937]">
            <div>
              <p className="font-medium">Wartungsmodus</p>
              <p className="text-sm text-zinc-500">
                Wenn aktiviert, können sich nur Super-Admins anmelden
              </p>
            </div>
            <Switch
              checked={maintenanceMode}
              onCheckedChange={toggleMaintenanceMode}
              disabled={saving}
            />
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label>Wartungsnachricht (optional)</Label>
            <Input
              value={maintenanceMessage}
              onChange={(e) => setMaintenanceMessage(e.target.value)}
              placeholder="z.B. System wird aktualisiert. Bitte versuchen Sie es später erneut."
              className="bg-[#1F2937]/50 border-[#DC2626]/30"
            />
            <p className="text-xs text-zinc-500">
              Diese Nachricht wird Benutzern angezeigt, die versuchen sich anzumelden.
            </p>
          </div>

          <Button onClick={saveMessage} disabled={saving} variant="outline" className="border-[#DC2626]/30 hover:bg-[#DC2626]/20">
            <Save className="h-4 w-4 mr-2" />
            Nachricht speichern
          </Button>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-[#1F2937] bg-black/40">
        <CardHeader>
          <CardTitle className="text-sm uppercase tracking-wider text-zinc-400">
            Informationen
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-zinc-400">
          <div className="space-y-2">
            <p><strong className="text-zinc-300">Während des Wartungsmodus:</strong></p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Nur Super-Admins können sich anmelden</li>
              <li>Registrierungen sind deaktiviert</li>
              <li>Normale Benutzer sehen eine Fehlermeldung beim Login</li>
              <li>Bestehende Sessions bleiben aktiv</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Color options for team members
const COLORS = [
  "#3b82f6", "#ef4444", "#22c55e", "#f59e0b", "#8b5cf6", 
  "#ec4899", "#06b6d4", "#84cc16", "#f97316", "#6366f1"
];

function App() {
  // Auth State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [authToken, setAuthToken] = useState(sessionStorage.getItem('authToken') || '');
  const [currentUser, setCurrentUser] = useState(null);
  
  // Auth Forms
  const [authMode, setAuthMode] = useState('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [tenantType, setTenantType] = useState('individual');
  const [registerEmail, setRegisterEmail] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');

  // Password Reset
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [resetStep, setResetStep] = useState('request'); // 'request' or 'reset'
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  
  // Pending users notification for super admin
  const [pendingUsersCount, setPendingUsersCount] = useState(0);
  
  // App State
  const [activeTab, setActiveTab] = useState('dashboard');
  const [tenant, setTenant] = useState(null);
  const [einsaetze, setEinsaetze] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [webhookKey, setWebhookKey] = useState('');
  
  // Password Change Dialog
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Einsatz Detail Dialog
  const [showEinsatzDialog, setShowEinsatzDialog] = useState(false);
  const [selectedEinsatz, setSelectedEinsatz] = useState(null);
  const [alarmiertFilter, setAlarmiertFilter] = useState(null); // null = alle, true = alarmiert, false = manuell
  
  // Manual Einsatz Dialog
  const [manualEinsatzDialogOpen, setManualEinsatzDialogOpen] = useState(false);
  const [manualEinsatzKeyword, setManualEinsatzKeyword] = useState('');
  const [manualEinsatzUnit, setManualEinsatzUnit] = useState('');
  const [manualEinsatzDate, setManualEinsatzDate] = useState('');
  
  // Participation Dialog
  const [showParticipationDialog, setShowParticipationDialog] = useState(false);
  const [selectedEinsatzForParticipation, setSelectedEinsatzForParticipation] = useState(null);
  const [selectedMemberForParticipation, setSelectedMemberForParticipation] = useState(null);
  const [participationDetails, setParticipationDetails] = useState({
    participated: true,
    function: "",
    vehicle: ""
  });
  
  // User's tenant settings (vehicles, functions)
  const [myTenantSettings, setMyTenantSettings] = useState(null);
  
  // Admin State
  const [pendingTenants, setPendingTenants] = useState([]);
  const [allTenants, setAllTenants] = useState([]);

  // Auth header helper
  const authHeader = useCallback(() => {
    return authToken ? { Authorization: `Bearer ${authToken}` } : {};
  }, [authToken]);

  // Check authentication
  const checkAuth = useCallback(async () => {
    if (!authToken) {
      setAuthChecking(false);
      return;
    }
    
    try {
      const res = await axios.get(`${API}/api/auth/me`, { headers: authHeader() });
      setCurrentUser(res.data);
      setTenant(res.data.tenant);
      setIsAuthenticated(true);
      // Reset to dashboard for non-admins if on admin-only tab
      if (res.data.role !== 'super_admin' && (activeTab === 'admin' || activeTab === 'users')) {
        setActiveTab('dashboard');
      }
    } catch (e) {
      sessionStorage.removeItem('authToken');
      setAuthToken('');
      setIsAuthenticated(false);
    } finally {
      setAuthChecking(false);
    }
  }, [authToken, authHeader, activeTab]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const res = await axios.post(`${API}/api/auth/login`, {
        username: username.toLowerCase(),
        password,
        totp_code: totpCode || undefined
      });
      
      if (res.data.requires_2fa) {
        setRequires2FA(true);
        return;
      }
      
      const token = res.data.token;
      setAuthToken(token);
      sessionStorage.setItem('authToken', token);
      setCurrentUser(res.data.user);
      setIsAuthenticated(true);
      setActiveTab('dashboard'); // Always show dashboard after login
      toast.success('Erfolgreich angemeldet', { duration: 2000 });
      
      setUsername('');
      setPassword('');
      setTotpCode('');
      setRequires2FA(false);
    } catch (e) {
      setLoginError(e.response?.data?.detail || 'Login fehlgeschlagen');
    }
  };

  // Password Reset Functions
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetMessage("");
    try {
      const res = await axios.post(`${API}/api/auth/forgot-password`, { email: resetEmail });
      setResetMessage(res.data.message);
      toast.success("E-Mail wurde gesendet (falls Konto existiert)");
    } catch (err) {
      setResetMessage(err.response?.data?.detail || "Fehler beim Senden der E-Mail");
      toast.error(err.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen haben");
      return;
    }
    setResetLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token: resetToken, new_password: newPassword });
      toast.success("Passwort erfolgreich geändert!");
      setShowForgotPassword(false);
      setResetStep("request");
      setResetToken("");
      setNewPassword("");
      setConfirmPassword("");
      setResetEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Zurücksetzen");
    } finally {
      setResetLoading(false);
    }
  };

  // Check for reset token in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("reset_token");
    if (token) {
      setResetToken(token);
      setResetStep("reset");
      setShowForgotPassword(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Register
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    if (password.length < 6) {
      setLoginError('Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    
    // Check password confirmation
    if (password !== passwordConfirm) {
      setLoginError('Passwörter stimmen nicht überein');
      return;
    }
    
    try {
      // Validate required fields
      if (!registerEmail || !registerEmail.includes('@')) {
        setLoginError('Bitte geben Sie eine gültige E-Mail-Adresse ein');
        return;
      }
      
      const res = await axios.post(`${API}/api/auth/register`, {
        username: username.toLowerCase(),
        password,
        password_confirm: passwordConfirm,
        tenant_name: tenantName,
        tenant_type: tenantType,
        email: registerEmail
      });
      
      // Check if registration was successful (user object in response)
      if (res.data && (res.data.user || res.data.success)) {
        toast.success('Registrierung erfolgreich! Sie erhalten eine Bestätigungs-E-Mail.');
        setAuthMode('login');
        setUsername('');
        setPassword('');
        setPasswordConfirm('');
        setTenantName('');
        setRegisterEmail('');
      } else {
        setLoginError('Unerwartete Antwort vom Server');
      }
    } catch (e) {
      console.error('Registration error:', e);
      const detail = e.response?.data?.detail;
      const errorMsg = typeof detail === 'object' ? JSON.stringify(detail) : (detail || 'Registrierung fehlgeschlagen');
      setLoginError(errorMsg);
    }
  };

  // Logout
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { headers: authHeader() });
    } catch (e) {
      console.error(e);
    }
    sessionStorage.removeItem('authToken');
    setAuthToken('');
    setIsAuthenticated(false);
    setCurrentUser(null);
    toast.success('Abgemeldet');
  };

  // Change own password
  const handleChangeOwnPassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Alle Felder müssen ausgefüllt werden');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Neues Passwort muss mindestens 8 Zeichen haben');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    
    try {
      await axios.put(`${API}/api/users/me/password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { headers: authHeader() });
      
      toast.success('Passwort wurde geändert');
      setShowPasswordDialog(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e) {
      const detail = e.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Fehler beim Ändern des Passworts';
      toast.error(errorMsg);
    }
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      // Filter-Parameter für alarmiert
      const filterParam = alarmiertFilter !== null ? `&alarmiert=${alarmiertFilter}` : '';
      
      const [einsaetzeRes, statsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/api/einsaetze/?count=50${filterParam}`, { headers: authHeader() }),
        axios.get(`${API}/api/einsaetze/stats`, { headers: authHeader() }),
        axios.get(`${API}/api/settings/`, { headers: authHeader() }).catch(() => ({ data: {} }))
      ]);
      
      setEinsaetze(einsaetzeRes.data.data);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
      console.log('Loaded settings:', settingsRes.data);
      console.log('Available vehicles:', settingsRes.data?.available_vehicles);
    } catch (e) {
      console.error('Fetch error:', e);
    }
  }, [isAuthenticated, authHeader, alarmiertFilter]);

  // Fetch webhook key
  const fetchWebhookKey = useCallback(async () => {
    if (!isAuthenticated || !currentUser) return;
    
    try {
      const res = await axios.get(`${API}/api/tenant/webhook-key`, { headers: authHeader() });
      setWebhookKey(res.data.webhook_key);
    } catch (e) {
      console.error('Webhook key fetch error:', e);
    }
  }, [isAuthenticated, currentUser, authHeader]);

  // Fetch user's tenant settings (vehicles, functions)
  const fetchMyTenantSettings = useCallback(async () => {
    if (!isAuthenticated || !currentUser) return;
    
    try {
      const res = await axios.get(`${API}/api/tenant/settings`, { headers: authHeader() });
      setMyTenantSettings(res.data);
    } catch (e) {
      console.error('Tenant settings fetch error:', e);
    }
  }, [isAuthenticated, currentUser, authHeader]);

  // Fetch admin data
  const fetchAdminData = useCallback(async () => {
    if (!currentUser || currentUser.role !== 'super_admin') return;
    
    try {
      const [pendingRes, allRes, pendingUsersRes] = await Promise.all([
        axios.get(`${API}/api/admin/tenants?status=pending`, { headers: authHeader() }),
        axios.get(`${API}/api/admin/tenants`, { headers: authHeader() }),
        axios.get(`${API}/api/admin/users/pending-count`, { headers: authHeader() }).catch(() => ({ data: { count: 0 } }))
      ]);
      
      setPendingTenants(pendingRes.data.data);
      setAllTenants(allRes.data.data);
      setPendingUsersCount(pendingUsersRes.data.count || 0);
    } catch (e) {
      console.error('Admin fetch error:', e);
    }
  }, [currentUser, authHeader]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
      fetchWebhookKey();
      fetchMyTenantSettings();
      fetchAdminData();
    }
  }, [isAuthenticated, fetchData, fetchWebhookKey, fetchMyTenantSettings, fetchAdminData]);

  // Close/End an Einsatz
  const handleCloseEinsatz = async (einsatzId) => {
    try {
      await axios.put(
        `${API}/api/einsaetze/${einsatzId}`,
        { active: false },
        { headers: authHeader() }
      );
      
      // Update local state
      setEinsaetze(prev => prev.map(e => 
        e.id === einsatzId ? { ...e, active: false } : e
      ));
      
      toast.success('Einsatz beendet');
    } catch (e) {
      toast.error('Fehler beim Beenden des Einsatzes');
    }
  };

  // Open self participation dialog (for individuals without team members)
  const openSelfParticipationDialog = (einsatz, didParticipate) => {
    setSelectedEinsatzForParticipation(einsatz);
    // Use a "self" marker instead of a team member
    setSelectedMemberForParticipation({ 
      id: 'self', 
      name: currentUser?.display_name || currentUser?.username || 'Ich',
      color: '#3b82f6'
    });
    const existingParticipation = einsatz.my_participation || {};
    setParticipationDetails({
      participated: didParticipate,
      function: existingParticipation.function || "",
      vehicle: existingParticipation.vehicle || ""
    });
    setShowParticipationDialog(true);
  };

  // Save participation with full details
  const saveParticipationWithDetails = async () => {
    if (!selectedEinsatzForParticipation || !selectedMemberForParticipation) return;
    
    try {
      // Save self participation
      await axios.put(
        `${API}/api/einsaetze/${selectedEinsatzForParticipation.id}/my-participation/`,
        {
          participated: participationDetails.participated,
          function: participationDetails.function || null,
          vehicle: participationDetails.vehicle || null
        },
        { headers: authHeader() }
      );
      
      // Update local state for self participation
      setEinsaetze(prev => prev.map(e => {
        if (e.id === selectedEinsatzForParticipation.id) {
          return {
            ...e,
            my_participation: {
              participated: participationDetails.participated,
              function: participationDetails.function,
              vehicle: participationDetails.vehicle
            }
          };
        }
        return e;
      }));
      
      // Also update selectedEinsatz if it's open
      if (selectedEinsatz?.id === selectedEinsatzForParticipation.id) {
        setSelectedEinsatz(prev => ({
          ...prev,
          my_participation: {
            participated: participationDetails.participated,
            function: participationDetails.function,
            vehicle: participationDetails.vehicle
          }
        }));
      }
      
      toast.success('Teilnahme gespeichert');
      setShowParticipationDialog(false);
      setSelectedEinsatzForParticipation(null);
      setSelectedMemberForParticipation(null);
      fetchData();
    } catch (e) {
      toast.error('Fehler beim Speichern');
    }
  };

  // Delete Einsatz
  const deleteEinsatz = async (einsatzId) => {
    if (!window.confirm('Einsatz wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden!')) return;
    
    try {
      await axios.delete(`${API}/api/einsaetze/${einsatzId}`, { headers: authHeader() });
      toast.success('Einsatz gelöscht');
      setShowEinsatzDialog(false);
      setSelectedEinsatz(null);
      fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Fehler beim Löschen');
    }
  };

  // Approve tenant
  const approveTenant = async (tenantId) => {
    try {
      await axios.post(`${API}/api/admin/tenants/${tenantId}/approve`, {}, { headers: authHeader() });
      toast.success('Tenant freigeschaltet');
      fetchAdminData();
    } catch (e) {
      toast.error('Fehler beim Freischalten');
    }
  };

  // Regenerate webhook key
  const regenerateWebhookKey = async () => {
    if (!window.confirm('Webhook-Key wirklich neu generieren? Der alte Key wird ungültig!')) return;
    
    try {
      const res = await axios.post(`${API}/api/tenant/regenerate-webhook-key`, {}, { headers: authHeader() });
      setWebhookKey(res.data.webhook_key);
      toast.success('Webhook-Key neu generiert');
    } catch (e) {
      toast.error('Fehler beim Generieren');
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('In Zwischenablage kopiert');
  };

  // Loading Screen
  if (authChecking) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="text-center">
          <Flame className="h-16 w-16 text-red-600 mx-auto mb-4 animate-pulse" />
          <p className="text-zinc-400">Lade...</p>
        </div>
      </div>
    );
  }

  // Login/Register Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1F2937]/20 to-[#0a0a0a] text-white flex items-center justify-center p-4">
        <Toaster position="top-right" theme="dark" richColors />
        
        <Card className="w-full max-w-md border-[#DC2626]/30 bg-gradient-to-br from-[#1F2937]/80 to-[#0a0a0a]/90 shadow-xl shadow-[#DC2626]/10">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-gradient-to-br from-[#DC2626]/30 to-[#FF8C00]/20 rounded-full border border-[#DC2626]/30">
                <Flame className="h-12 w-12 text-[#DC2626]" />
              </div>
            </div>
            <CardTitle className="font-bold text-3xl tracking-wide">
              <span className="text-[#DC2626]">Lüne</span>
              <span className="text-[#FF8C00]">fire</span>
            </CardTitle>
            <p className="text-[10px] text-zinc-400 uppercase tracking-widest mt-1">IT Media Solutions</p>
            <Badge variant="outline" className="mx-auto mt-3 border-[#FF8C00]/50 text-[#FF8C00]">
              Hosting Edition
            </Badge>
          </CardHeader>
          
          <CardContent>
            <Tabs value={authMode} onValueChange={setAuthMode}>
              <TabsList className="grid w-full grid-cols-2 bg-[#1F2937] mb-4">
                <TabsTrigger value="login" className="data-[state=active]:bg-[#DC2626] data-[state=active]:text-white">Anmelden</TabsTrigger>
                <TabsTrigger value="register" className="data-[state=active]:bg-[#DC2626] data-[state=active]:text-white">Registrieren</TabsTrigger>
              </TabsList>
              
              {/* Login Form */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Benutzername</Label>
                    <Input
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="benutzername"
                      className="bg-[#1F2937]/50 border-[#DC2626]/30 focus:border-[#DC2626]"
                      autoFocus
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Passwort</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Passwort"
                        className="bg-[#1F2937]/50 border-[#DC2626]/30 focus:border-[#DC2626] pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-[#FF8C00]"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  
                  {requires2FA && (
                    <div className="space-y-2">
                      <Label>2FA Code</Label>
                      <Input
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value)}
                        placeholder="123456"
                        className="bg-[#1F2937]/50 border-[#DC2626]/30 focus:border-[#DC2626]"
                        maxLength={6}
                      />
                    </div>
                  )}
                  
                  {loginError && (
                    <div className="p-3 bg-[#DC2626]/20 border border-[#DC2626]/50 rounded text-[#DC2626] text-sm">
                      {loginError}
                    </div>
                  )}
                  
                  <Button type="submit" className="w-full bg-gradient-to-r from-[#DC2626] to-[#FF8C00] hover:from-[#DC2626]/90 hover:to-[#FF8C00]/90 text-white font-semibold">
                    Anmelden
                  </Button>

                  <div className="text-center mt-2">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-[#FF8C00] hover:text-[#DC2626] hover:underline"
                    >
                      Passwort vergessen?
                    </button>
                  </div>
                </form>
              </TabsContent>
              
              {/* Register Form */}
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Benutzername</Label>
                    <Input
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="benutzername"
                      className="bg-[#1F2937]/50 border-[#DC2626]/30 focus:border-[#DC2626]"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>E-Mail <span className="text-[#DC2626]">*</span></Label>
                    <Input
                      type="email"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      placeholder="ihre@email.de"
                      className="bg-[#1F2937]/50 border-[#DC2626]/30 focus:border-[#DC2626]"
                      required
                    />
                    <p className="text-xs text-zinc-500">Sie erhalten eine Bestätigungs-E-Mail nach der Registrierung</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Passwort</Label>
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min. 6 Zeichen"
                      className="bg-[#1F2937]/50 border-[#DC2626]/30 focus:border-[#DC2626]"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Passwort bestätigen</Label>
                    <Input
                      type="password"
                      value={passwordConfirm}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      placeholder="Passwort wiederholen"
                      className="bg-[#1F2937]/50 border-[#DC2626]/30 focus:border-[#DC2626]"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Name (Feuerwehr oder Ihr Name)</Label>
                    <Input
                      value={tenantName}
                      onChange={(e) => setTenantName(e.target.value)}
                      placeholder="z.B. FF Musterstadt oder Max Mustermann"
                      className="bg-[#1F2937]/50 border-[#DC2626]/30 focus:border-[#DC2626]"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Typ</Label>
                    <Select value={tenantType} onValueChange={setTenantType}>
                      <SelectTrigger className="bg-[#1F2937]/50 border-[#DC2626]/30">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1F2937] border-[#DC2626]/30">
                        <SelectItem value="individual">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-[#FF8C00]" /> Einzelperson
                          </div>
                        </SelectItem>
                        <SelectItem value="group">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-[#FF8C00]" /> Feuerwehr / Gruppe
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {loginError && (
                    <div className="p-3 bg-[#DC2626]/20 border border-[#DC2626]/50 rounded text-[#DC2626] text-sm">
                      {loginError}
                    </div>
                  )}
                  
                  <Button type="submit" className="w-full bg-gradient-to-r from-[#FF8C00] to-[#FFD700] hover:from-[#FF8C00]/90 hover:to-[#FFD700]/90 text-[#1F2937] font-semibold">
                    Registrieren
                  </Button>
                  
                  <p className="text-xs text-zinc-500 text-center">
                    Nach der Registrierung wird Ihr Account von einem Administrator freigeschaltet.
                  </p>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Handle tenant change
  const handleTenantSwitch = async (tenantId) => {
    try {
      const res = await axios.post(`${API}/api/auth/switch-tenant`, {
        tenant_id: tenantId
      }, { headers: authHeader() });
      
      if (res.data.token) {
        sessionStorage.setItem('authToken', res.data.token);
        setAuthToken(res.data.token);
      }
      setCurrentUser(res.data.user);
      toast.success('Tenant gewechselt');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Wechseln');
    }
  };

  // Main App
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Toaster position="top-right" theme="dark" richColors />
      
      {/* Header - Lünefire Style */}
      <header className="sticky top-0 z-50 border-b border-[#DC2626]/30 bg-gradient-to-r from-[#1F2937]/95 to-[#0a0a0a]/95 backdrop-blur-md">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Lünefire Logo */}
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Flame className="h-10 w-10 text-[#DC2626]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#FF8C00] to-transparent opacity-50 rounded-full"></div>
                </div>
                <div>
                  <h1 className="font-bold text-xl tracking-wide">
                    <span className="text-[#DC2626]">Lüne</span>
                    <span className="text-[#FF8C00]">fire</span>
                  </h1>
                  <p className="text-[10px] text-zinc-400 uppercase tracking-widest">IT Media Solutions</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Tenant Selector for multi-tenant users */}
              {currentUser?.tenants && currentUser.tenants.length > 1 && (
                <Select 
                  value={currentUser?.active_tenant_id || currentUser?.tenant_id || ''} 
                  onValueChange={handleTenantSwitch}
                >
                  <SelectTrigger className="w-48 bg-[#1F2937]/50 border-[#DC2626]/30 text-sm hover:border-[#DC2626]/60">
                    <Building2 className="h-4 w-4 mr-2 text-[#FF8C00]" />
                    <SelectValue placeholder="Tenant wählen...">
                      {currentUser?.tenants?.find(t => t.id === (currentUser?.active_tenant_id || currentUser?.tenant_id))?.name || 'Tenant wählen...'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-[#1F2937] border-[#DC2626]/30">
                    {currentUser.tenants.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {/* User Info Display */}
              <div className="flex items-center gap-2 px-3 py-1 bg-[#1F2937]/50 rounded-lg border border-[#DC2626]/30">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#DC2626] to-[#FF8C00] flex items-center justify-center text-white font-bold text-sm">
                  {(currentUser?.display_name || currentUser?.username || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-white">{currentUser?.display_name || currentUser?.username}</span>
                  <span className="text-xs text-zinc-400">
                    {currentUser?.role === 'super_admin' ? 'Super Admin' : 
                     currentUser?.role === 'tenant_admin' ? 'Admin' : 'Mitglied'}
                  </span>
                </div>
              </div>
              
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowPasswordDialog(true)}
                title="Passwort ändern"
                className="hover:bg-[#DC2626]/20 hover:text-[#FF8C00]"
              >
                <Key className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="hover:bg-[#DC2626]/20 hover:text-[#FF8C00]">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Navigation - Lünefire Style */}
      <nav className="border-b border-[#DC2626]/20 bg-gradient-to-r from-[#1F2937]/80 to-[#1F2937]/60">
        <div className="container mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto">
            <Button 
              variant={activeTab === 'dashboard' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('dashboard')}
              className={activeTab === 'dashboard' ? 'bg-[#DC2626] text-white hover:bg-[#DC2626]/90' : 'hover:bg-[#DC2626]/20 hover:text-[#FF8C00]'}
            >
              <BarChart3 className="h-4 w-4 mr-2" /> Dashboard
            </Button>
            <Button 
              variant={activeTab === 'einsaetze' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('einsaetze')}
              className={activeTab === 'einsaetze' ? 'bg-[#DC2626] text-white hover:bg-[#DC2626]/90' : 'hover:bg-[#DC2626]/20 hover:text-[#FF8C00]'}
            >
              <Flame className="h-4 w-4 mr-2" /> Einsätze
            </Button>
            <Button 
              variant={activeTab === 'charts' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('charts')}
              className={activeTab === 'charts' ? 'bg-[#DC2626] text-white hover:bg-[#DC2626]/90' : 'hover:bg-[#DC2626]/20 hover:text-[#FF8C00]'}
            >
              <BarChart3 className="h-4 w-4 mr-2" /> Diagramme
            </Button>
            <Button 
              variant={activeTab === 'settings' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('settings')}
              className={activeTab === 'settings' ? 'bg-[#DC2626] text-white hover:bg-[#DC2626]/90' : 'hover:bg-[#DC2626]/20 hover:text-[#FF8C00]'}
            >
              <Settings className="h-4 w-4 mr-2" /> Einstellungen
            </Button>
            {currentUser?.role === 'super_admin' && (
              <Button 
                variant={activeTab === 'admin' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('admin')}
                className={`relative ${activeTab === 'admin' ? 'bg-[#DC2626] text-white hover:bg-[#DC2626]/90' : 'hover:bg-[#DC2626]/20 hover:text-[#FF8C00]'}`}
              >
                <Shield className="h-4 w-4 mr-2" /> Admin
                {pendingUsersCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#FF8C00] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                    {pendingUsersCount}
                  </span>
                )}
              </Button>
            )}
          </div>
        </div>
      </nav>
      
      {/* Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            
            {/* Active Alarm Card - Lünefire Style */}
            <Card 
              className={`md:col-span-2 border-[#1F2937] bg-gradient-to-br from-[#1F2937]/60 to-[#0a0a0a]/80 ${einsaetze.find(e => e.active) ? 'border-[#DC2626] shadow-lg shadow-[#DC2626]/20' : ''}`}
            >
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 font-heading uppercase tracking-wider text-sm text-zinc-400">
                  <Flame className={`h-5 w-5 ${einsaetze.find(e => e.active) ? 'text-[#DC2626]' : 'text-zinc-500'}`} />
                  Aktueller Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                {einsaetze.find(e => e.active) ? (
                  <div className="space-y-6">
                    {(() => {
                      const activeEinsatz = einsaetze.find(e => e.active);
                      return (
                        <>
                          <div>
                            <p className="font-heading text-4xl md:text-5xl font-black text-[#DC2626] uppercase tracking-tight">
                              {activeEinsatz.keyword}
                            </p>
                            <p className="font-mono text-lg text-zinc-400 mt-2">
                              Einheit: {activeEinsatz.unit || '-'}
                            </p>
                            {activeEinsatz.vehicles && (
                              <p className="font-mono text-base text-[#FF8C00] mt-2">
                                <Truck className="inline h-4 w-4 mr-2" />
                                {activeEinsatz.vehicles}
                              </p>
                            )}
                            <p className="font-mono text-sm text-zinc-500 mt-2">
                              <Clock className="inline h-4 w-4 mr-1" />
                              {new Date(activeEinsatz.timestamp).toLocaleString('de-DE')}
                            </p>
                          </div>
                          
                          {/* Meine Teilnahme */}
                          <div className="space-y-4 mt-6 pt-4 border-t border-[#1F2937]">
                            <p className="text-sm font-semibold flex items-center gap-2 text-[#FF8C00]">
                              <div className="w-2 h-2 rounded-full bg-[#FF8C00]"></div>
                              {currentUser?.display_name || currentUser?.username || 'Meine Teilnahme'}
                              {activeEinsatz.my_participation?.participated !== undefined && (
                                <Badge className={`ml-2 ${
                                  activeEinsatz.my_participation?.participated 
                                    ? 'bg-emerald-900/50 text-emerald-400 border-emerald-700' 
                                    : 'bg-[#DC2626]/20 text-[#DC2626] border-[#DC2626]/50'
                                }`}>
                                  {activeEinsatz.my_participation?.participated ? '✓ Dabei' : '✗ Nicht dabei'}
                                </Badge>
                              )}
                            </p>
                            {activeEinsatz.my_participation?.participated && (activeEinsatz.my_participation?.function || activeEinsatz.my_participation?.vehicle) && (
                              <div className="text-sm text-zinc-400">
                                {activeEinsatz.my_participation.function && <span className="text-[#FF8C00]">{activeEinsatz.my_participation.function}</span>}
                                {activeEinsatz.my_participation.function && activeEinsatz.my_participation.vehicle && <span className="mx-2">•</span>}
                                {activeEinsatz.my_participation.vehicle && <span className="text-[#FFD700]">{activeEinsatz.my_participation.vehicle}</span>}
                              </div>
                            )}
                            <div className="flex gap-2">
                              <Button
                                size="lg"
                                className={`flex-1 h-12 font-bold uppercase tracking-wider ${
                                  activeEinsatz.my_participation?.participated === true 
                                    ? 'bg-emerald-700' 
                                    : 'bg-emerald-600 hover:bg-emerald-700'
                                }`}
                                onClick={() => openSelfParticipationDialog(activeEinsatz, true)}
                              >
                                <CheckCircle2 className="mr-2 h-5 w-5" />
                                Dabei
                              </Button>
                              <Button
                                size="lg"
                                variant="destructive"
                                className={`flex-1 h-12 font-bold uppercase tracking-wider ${
                                  activeEinsatz.my_participation?.participated === false 
                                    ? 'bg-red-800' 
                                    : 'bg-red-700 hover:bg-red-800'
                                }`}
                                onClick={() => openSelfParticipationDialog(activeEinsatz, false)}
                              >
                                <XCircle className="mr-2 h-5 w-5" />
                                Nicht Dabei
                              </Button>
                            </div>
                          </div>
                          
                          {/* Einsatz beenden Button */}
                          <div className="mt-6 pt-4 border-t border-[#1F2937]">
                            <Button
                              size="lg"
                              variant="outline"
                              className="w-full h-12 font-bold uppercase tracking-wider border-zinc-600 text-zinc-400 hover:bg-[#1F2937] hover:text-white"
                              onClick={() => handleCloseEinsatz(activeEinsatz.id)}
                            >
                              <X className="mr-2 h-5 w-5" />
                              Einsatz Beenden
                            </Button>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Activity className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
                    <p className="font-heading text-2xl text-zinc-500 uppercase tracking-wider">
                      Kein aktiver Einsatz
                    </p>
                    <p className="text-zinc-600 mt-2 font-mono text-sm">
                      Bereit für den nächsten Alarm
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Stats Card */}
            <Card className="border-[#1F2937] bg-black/40">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 font-heading uppercase tracking-wider text-sm text-zinc-400">
                  <TrendingUp className="h-5 w-5 text-[#FF8C00]" />
                  Statistik {stats?.year}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Gesamt Einsätze */}
                <div className="bg-[#1F2937]/80/50 rounded-sm p-4 border border-[#1F2937]">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-mono text-3xl font-bold text-white">
                        {stats?.total || 0}
                      </p>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Einsätze {stats?.year}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-lg text-zinc-500">
                        {stats?.total_all || 0}
                      </p>
                      <p className="text-xs text-zinc-600 uppercase tracking-wider mt-1">Gesamt</p>
                    </div>
                  </div>
                </div>

                {/* Alarmiert vs Manuell */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-amber-900/20 rounded-sm p-3 border border-amber-800 text-center">
                    <Bell className="h-4 w-4 text-[#FF8C00] mx-auto mb-1" />
                    <p className="font-mono text-xl font-bold text-[#FFD700]">{stats?.alarmiert_count || 0}</p>
                    <p className="text-xs text-zinc-500">Alarmiert</p>
                  </div>
                  <div className="bg-[#1F2937]/80/50 rounded-sm p-3 border border-[#1F2937]/80 text-center">
                    <BellOff className="h-4 w-4 text-zinc-500 mx-auto mb-1" />
                    <p className="font-mono text-xl font-bold text-zinc-400">{stats?.manuell_count || 0}</p>
                    <p className="text-xs text-zinc-500">Manuell</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Einsätze Table */}
            <Card className="md:col-span-2 lg:col-span-3 border-[#1F2937] bg-black/40">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 font-heading uppercase tracking-wider text-sm text-zinc-400">
                    <AlertTriangle className="h-5 w-5 text-[#DC2626]" />
                    Letzte Einsätze
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {/* Button für manuellen Einsatz */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setManualEinsatzDialogOpen(true)}
                      className="border-emerald-700 text-emerald-400 hover:bg-emerald-600/20"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Einsatz anlegen
                    </Button>
                    {/* Filter für Alarmiert */}
                    <Filter className="h-4 w-4 text-zinc-500" />
                    <Select 
                      value={alarmiertFilter === null ? "alle" : alarmiertFilter ? "alarmiert" : "manuell"} 
                      onValueChange={(v) => setAlarmiertFilter(v === "alle" ? null : v === "alarmiert")}
                    >
                      <SelectTrigger className="w-[140px] h-8 bg-[#1F2937] border-[#1F2937]/80 text-sm">
                        <SelectValue placeholder="Filter" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1F2937]/80 border-[#1F2937]/80">
                        <SelectItem value="alle">Alle</SelectItem>
                        <SelectItem value="alarmiert">
                          <span className="flex items-center gap-2">
                            <Bell className="h-3 w-3 text-[#FF8C00]" />
                            Alarmiert
                          </span>
                        </SelectItem>
                        <SelectItem value="manuell">
                          <span className="flex items-center gap-2">
                            <BellOff className="h-3 w-3 text-zinc-500" />
                            Manuell
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {einsaetze.length === 0 ? (
                  <div className="text-center py-8">
                    <Activity className="h-12 w-12 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500">Noch keine Einsätze vorhanden</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[#1F2937] hover:bg-transparent">
                          <TableHead className="font-heading uppercase tracking-wider text-zinc-500">Stichwort</TableHead>
                          <TableHead className="font-heading uppercase tracking-wider text-zinc-500">Einheit</TableHead>
                          <TableHead className="font-heading uppercase tracking-wider text-zinc-500">Fahrzeuge</TableHead>
                          <TableHead className="font-heading uppercase tracking-wider text-zinc-500">Zeit</TableHead>
                          <TableHead className="font-heading uppercase tracking-wider text-zinc-500 text-center" title="Alarmiert">
                            <Bell className="h-4 w-4 mx-auto" />
                          </TableHead>
                          <TableHead className="font-heading uppercase tracking-wider text-zinc-500">Status</TableHead>
                          <TableHead className="font-heading uppercase tracking-wider text-zinc-500">Teilnahme</TableHead>
                          <TableHead className="font-heading uppercase tracking-wider text-zinc-500 w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {einsaetze.slice(0, 10).map((einsatz) => (
                          <TableRow 
                            key={einsatz.id} 
                            className="border-[#1F2937] hover:bg-[#1F2937]/80/50 cursor-pointer"
                            onClick={() => { setSelectedEinsatz(einsatz); setShowEinsatzDialog(true); }}
                          >
                            <TableCell className="font-mono font-bold text-white">
                              {einsatz.keyword}
                            </TableCell>
                            <TableCell className="font-mono text-zinc-400">
                              {einsatz.unit || '-'}
                            </TableCell>
                            <TableCell className="font-mono text-sm text-[#FF8C00] max-w-[200px] truncate">
                              {einsatz.vehicles || "-"}
                            </TableCell>
                            <TableCell className="font-mono text-zinc-500 text-sm">
                              {new Date(einsatz.timestamp).toLocaleString('de-DE')}
                            </TableCell>
                            <TableCell className="text-center">
                              {einsatz.alarmiert ? (
                                <Bell className="h-4 w-4 text-[#FF8C00] mx-auto" title="Alarmiert" />
                              ) : (
                                <BellOff className="h-4 w-4 text-zinc-600 mx-auto" title="Manuell erstellt" />
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant="outline" 
                                className={einsatz.active ? 'border-red-700 text-[#DC2626] bg-red-900/20' : 'border-[#1F2937]/80 text-zinc-400'}
                              >
                                {einsatz.active ? 'Aktiv' : 'Abgeschlossen'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {/* Meine Teilnahme */}
                              <div className="flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className={`h-7 px-2 ${einsatz.my_participation?.participated === true ? 'bg-emerald-900/50 text-emerald-400' : 'text-zinc-500 hover:text-emerald-400'}`}
                                  onClick={(e) => { e.stopPropagation(); openSelfParticipationDialog(einsatz, true); }}
                                  title="Teilgenommen"
                                >
                                  <CheckCircle2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className={`h-7 px-2 ${einsatz.my_participation?.participated === false ? 'bg-red-900/50 text-[#DC2626]' : 'text-zinc-500 hover:text-[#DC2626]'}`}
                                  onClick={(e) => { e.stopPropagation(); openSelfParticipationDialog(einsatz, false); }}
                                  title="Nicht teilgenommen"
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                                {einsatz.my_participation?.participated && (einsatz.my_participation?.function || einsatz.my_participation?.vehicle) && (
                                  <div className="text-xs text-zinc-500 ml-1 truncate max-w-[80px]" title={`${einsatz.my_participation.function || ''} ${einsatz.my_participation.vehicle || ''}`}>
                                    {einsatz.my_participation.function && <span className="text-[#FF8C00]">{einsatz.my_participation.function}</span>}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-zinc-500 hover:text-[#DC2626] hover:bg-red-900/20"
                                onClick={(e) => { e.stopPropagation(); deleteEinsatz(einsatz.id); }}
                                title="Löschen"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Einsätze Tab */}
        {activeTab === 'einsaetze' && (
          <Card className="border-[#1F2937] bg-black/40">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm uppercase tracking-wider text-zinc-400">
                  Alle Einsätze
                </CardTitle>
                {/* Filter für Alarmiert */}
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-zinc-500" />
                  <Select 
                    value={alarmiertFilter === null ? "alle" : alarmiertFilter ? "alarmiert" : "manuell"} 
                    onValueChange={(v) => setAlarmiertFilter(v === "alle" ? null : v === "alarmiert")}
                  >
                    <SelectTrigger className="w-[140px] h-8 bg-[#1F2937] border-[#1F2937]/80 text-sm">
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1F2937]/80 border-[#1F2937]/80">
                      <SelectItem value="alle">Alle</SelectItem>
                      <SelectItem value="alarmiert">
                        <span className="flex items-center gap-2">
                          <Bell className="h-3 w-3 text-[#FF8C00]" />
                          Alarmiert
                        </span>
                      </SelectItem>
                      <SelectItem value="manuell">
                        <span className="flex items-center gap-2">
                          <BellOff className="h-3 w-3 text-zinc-500" />
                          Manuell
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {einsaetze.length === 0 ? (
                <p className="text-zinc-500 text-center py-8">Noch keine Einsätze</p>
              ) : (
                <div className="space-y-2">
                  {einsaetze.map((e) => (
                    <div 
                      key={e.id} 
                      className="p-4 bg-[#1F2937]/80/50 rounded border border-[#1F2937] cursor-pointer hover:border-[#1F2937]/80"
                      onClick={() => { setSelectedEinsatz(e); setShowEinsatzDialog(true); }}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Flame className={`h-6 w-6 ${e.active ? 'text-[#DC2626] animate-pulse' : 'text-zinc-600'}`} />
                          <div>
                            <p className="font-bold text-lg">{e.keyword}</p>
                            <p className="text-sm text-zinc-500">
                              {new Date(e.timestamp).toLocaleString('de-DE')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Alarmiert Status */}
                          {e.alarmiert !== false ? (
                            <Bell className="h-5 w-5 text-[#FF8C00]" title="Alarmiert" />
                          ) : (
                            <BellOff className="h-5 w-5 text-zinc-600" title="Manuell erfasst" />
                          )}
                          {e.vehicles && (
                            <Badge className="bg-amber-600">{e.vehicles}</Badge>
                          )}
                        </div>
                      </div>
                      
                      {/* Meine Teilnahme */}
                      <div className="flex items-center gap-2">
                        {e.my_participation?.participated === true && (
                          <Badge className="bg-emerald-900/50 border border-emerald-700">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Dabei
                            {e.my_participation.function && <span className="ml-1 text-[#FF8C00]">• {e.my_participation.function}</span>}
                          </Badge>
                        )}
                        {e.my_participation?.participated === false && (
                          <Badge className="bg-red-900/50 border border-red-700">
                            <XCircle className="h-3 w-3 mr-1" />
                            Nicht dabei
                          </Badge>
                        )}
                        {e.my_participation?.participated === undefined && (
                          <Badge variant="outline" className="border-[#1F2937]/80 text-zinc-500">
                            Keine Angabe
                          </Badge>
                        )}
                        {/* Delete button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="ml-auto text-zinc-500 hover:text-[#DC2626] hover:bg-red-900/20"
                          onClick={(ev) => { ev.stopPropagation(); deleteEinsatz(e.id); }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Charts Tab */}
        {activeTab === 'charts' && (
          <ChartsPanel authHeader={authHeader} stats={stats} />
        )}
        
        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <SettingsPanel 
            settings={settings}
            webhookKey={webhookKey}
            currentUser={currentUser}
            authHeader={authHeader}
            copyToClipboard={copyToClipboard}
            regenerateWebhookKey={regenerateWebhookKey}
            fetchData={fetchData}
          />
        )}

        {/* Users Tab - Only for Super Admin */}
        {activeTab === 'users' && currentUser?.role === 'super_admin' && (
          <UsersPanel 
            currentUser={currentUser}
            authHeader={authHeader}
            tenant={tenant}
          />
        )}
        
        {/* Admin Tab with Sub-Tabs (Super Admin only) */}
        {activeTab === 'admin' && currentUser?.role === 'super_admin' && (
          <AdminPanel 
            authHeader={authHeader} 
            allTenants={allTenants}
            fetchAdminData={fetchAdminData}
            pendingUsersCount={pendingUsersCount}
          />
        )}
      </main>
      
      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={(open) => {
        setShowPasswordDialog(open);
        if (!open) {
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        }
      }}>
        <DialogContent className="bg-[#1F2937]/80 border-[#1F2937]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-[#FF8C00]" />
              Passwort ändern
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Aktuelles Passwort</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-[#1F2937] border-[#1F2937]/80"
              />
            </div>
            <div>
              <Label>Neues Passwort</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mindestens 8 Zeichen"
                className="bg-[#1F2937] border-[#1F2937]/80"
              />
            </div>
            <div>
              <Label>Neues Passwort bestätigen</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Passwort wiederholen"
                className="bg-[#1F2937] border-[#1F2937]/80"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowPasswordDialog(false)} className="flex-1 border-[#1F2937]/80">
                Abbrechen
              </Button>
              <Button onClick={handleChangeOwnPassword} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                Passwort ändern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Einsatz Detail Dialog */}
      <Dialog open={showEinsatzDialog} onOpenChange={setShowEinsatzDialog}>
        <DialogContent className="bg-[#1F2937]/80 border-[#1F2937] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flame className={`h-5 w-5 ${selectedEinsatz?.active ? 'text-[#DC2626]' : 'text-zinc-500'}`} />
              {selectedEinsatz?.keyword}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEinsatz && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-zinc-500">Datum/Zeit</p>
                  <p>{new Date(selectedEinsatz.timestamp).toLocaleString('de-DE')}</p>
                </div>
                {selectedEinsatz.vehicles && (
                  <div>
                    <p className="text-zinc-500">Fahrzeuge</p>
                    <p className="text-[#FFD700]">{selectedEinsatz.vehicles}</p>
                  </div>
                )}
              </div>
              
              <div className="border-t border-[#1F2937] pt-4">
                <p className="text-sm text-zinc-500 mb-3">Meine Teilnahme</p>
                <div className="space-y-3">
                  {/* Einzelperson-Modus: Eigene Teilnahme erfassen */}
                  <div className="flex items-center justify-between p-3 bg-[#1F2937]/50 rounded">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold bg-[#DC2626]">
                        {currentUser?.display_name?.charAt(0) || currentUser?.username?.charAt(0) || 'U'}
                      </div>
                      <span className="font-medium">{currentUser?.display_name || currentUser?.username || 'Ich'}</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {/* Show function and vehicle if participated */}
                      {selectedEinsatz.my_participation?.participated === true && (selectedEinsatz.my_participation?.function || selectedEinsatz.my_participation?.vehicle) && (
                        <div className="text-xs text-zinc-500 mr-2">
                          {selectedEinsatz.my_participation.function && <span className="text-[#FF8C00]">{selectedEinsatz.my_participation.function}</span>}
                          {selectedEinsatz.my_participation.function && selectedEinsatz.my_participation.vehicle && <span className="mx-1">•</span>}
                          {selectedEinsatz.my_participation.vehicle && <span className="text-[#FFD700]">{selectedEinsatz.my_participation.vehicle}</span>}
                        </div>
                      )}
                      <Button
                        size="sm"
                        variant={selectedEinsatz.my_participation?.participated === true ? 'default' : 'outline'}
                        className={selectedEinsatz.my_participation?.participated === true ? 'bg-emerald-600 hover:bg-emerald-700' : 'border-emerald-700 text-emerald-400'}
                        onClick={() => openSelfParticipationDialog(selectedEinsatz, true)}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        Dabei
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedEinsatz.my_participation?.participated === false ? 'default' : 'outline'}
                        className={selectedEinsatz.my_participation?.participated === false ? 'bg-[#DC2626] hover:bg-red-700' : 'border-red-700 text-[#DC2626]'}
                        onClick={() => openSelfParticipationDialog(selectedEinsatz, false)}
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Nicht dabei
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between border-t border-[#1F2937] pt-4">
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => deleteEinsatz(selectedEinsatz.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Einsatz löschen
                </Button>
                <Button variant="ghost" onClick={() => setShowEinsatzDialog(false)}>
                  <X className="h-4 w-4 mr-2" />
                  Schließen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual Einsatz Dialog */}
      <Dialog open={manualEinsatzDialogOpen} onOpenChange={setManualEinsatzDialogOpen}>
        <DialogContent className="bg-[#1F2937]/80 border-[#1F2937]/80 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-emerald-400" />
              Einsatz manuell anlegen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Stichwort / Keyword <span className="text-[#DC2626]">*</span></Label>
              <Input
                value={manualEinsatzKeyword}
                onChange={(e) => setManualEinsatzKeyword(e.target.value)}
                placeholder="z.B. B1, H1, TH1"
                className="bg-[#1F2937] border-[#1F2937]/80"
              />
            </div>
            <div className="space-y-2">
              <Label>Einheit (optional)</Label>
              <Input
                value={manualEinsatzUnit}
                onChange={(e) => setManualEinsatzUnit(e.target.value)}
                placeholder="z.B. FF Musterstadt"
                className="bg-[#1F2937] border-[#1F2937]/80"
              />
            </div>
            <div className="space-y-2">
              <Label>Datum/Uhrzeit (optional)</Label>
              <Input
                type="datetime-local"
                value={manualEinsatzDate}
                onChange={(e) => setManualEinsatzDate(e.target.value)}
                className="bg-[#1F2937] border-[#1F2937]/80"
              />
              <p className="text-xs text-zinc-500">Leer lassen für aktuelles Datum</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setManualEinsatzDialogOpen(false)} className="border-[#1F2937]/80">
                Abbrechen
              </Button>
              <Button
                onClick={async () => {
                  if (!manualEinsatzKeyword.trim()) {
                    toast.error('Bitte geben Sie ein Stichwort ein');
                    return;
                  }
                  try {
                    await axios.post(`${API}/api/einsaetze`, {
                      keyword: manualEinsatzKeyword,
                      unit: manualEinsatzUnit || '',
                      timestamp: manualEinsatzDate || null,
                      alarmiert: false
                    }, { headers: authHeader() });
                    toast.success('Einsatz angelegt');
                    setManualEinsatzDialogOpen(false);
                    setManualEinsatzKeyword('');
                    setManualEinsatzUnit('');
                    setManualEinsatzDate('');
                    fetchData();
                  } catch (e) {
                    toast.error('Fehler beim Anlegen des Einsatzes');
                  }
                }}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Anlegen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Participation Detail Dialog */}
      <Dialog open={showParticipationDialog} onOpenChange={setShowParticipationDialog}>
        <DialogContent className="bg-[#1F2937]/80 border-[#1F2937] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {selectedMemberForParticipation && (
                <>
                  <div 
                    className="w-6 h-6 rounded-full"
                    style={{ backgroundColor: selectedMemberForParticipation.color }}
                  />
                  Teilnahme für {selectedMemberForParticipation.name}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEinsatzForParticipation && selectedMemberForParticipation && (
            <div className="space-y-4 py-4">
              <p className="text-sm text-zinc-400">
                Einsatz: <span className="text-[#FF8C00] font-mono">{selectedEinsatzForParticipation.keyword}</span>
                <span className="text-zinc-600 ml-2">
                  ({new Date(selectedEinsatzForParticipation.timestamp).toLocaleString('de-DE')})
                </span>
              </p>

              {/* Teilnahme-Auswahl */}
              <div className="space-y-2">
                <Label className="text-zinc-300">Teilnahme</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={participationDetails.participated ? "default" : "outline"}
                    className={participationDetails.participated ? "bg-emerald-600 hover:bg-emerald-700 flex-1" : "border-[#1F2937]/80 flex-1"}
                    onClick={() => setParticipationDetails(p => ({ ...p, participated: true }))}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Teilgenommen
                  </Button>
                  <Button
                    type="button"
                    variant={!participationDetails.participated ? "default" : "outline"}
                    className={!participationDetails.participated ? "bg-[#DC2626] hover:bg-red-700 flex-1" : "border-[#1F2937]/80 flex-1"}
                    onClick={() => setParticipationDetails(p => ({ ...p, participated: false, function: "", vehicle: "" }))}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Nicht dabei
                  </Button>
                </div>
              </div>
              
              {participationDetails.participated && (
                <>
                  {/* Funktion */}
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Funktion</Label>
                    <Select
                      value={participationDetails.function}
                      onValueChange={(value) => {
                        if (value === "Reserve") {
                          setParticipationDetails(p => ({ ...p, function: value, vehicle: "-" }));
                        } else {
                          setParticipationDetails(p => ({ ...p, function: value }));
                        }
                      }}
                    >
                      <SelectTrigger className="bg-[#1F2937] border-[#1F2937]/80">
                        <SelectValue placeholder="Funktion wählen..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1F2937] border-[#1F2937]/80">
                        {(myTenantSettings?.available_functions || settings?.available_functions || ["Fahrzeugführer", "Maschinist", "Mannschaft", "Reserve"]).map((func) => (
                          <SelectItem key={func} value={func} className="text-white hover:bg-zinc-700">
                            {func}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Fahrzeug */}
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Fahrzeug</Label>
                    {participationDetails.function === "Reserve" ? (
                      <Input 
                        value="-" 
                        disabled 
                        className="bg-[#1F2937] border-[#1F2937]/80 text-zinc-500"
                      />
                    ) : (
                      <Select
                        value={participationDetails.vehicle}
                        onValueChange={(value) => setParticipationDetails(p => ({ ...p, vehicle: value }))}
                      >
                        <SelectTrigger className="bg-[#1F2937] border-[#1F2937]/80">
                          <SelectValue placeholder="Fahrzeug wählen..." />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1F2937] border-[#1F2937]/80">
                          {(myTenantSettings?.available_vehicles || settings?.available_vehicles || []).length > 0 ? (
                            (myTenantSettings?.available_vehicles || settings?.available_vehicles).map((vehicle) => (
                              <SelectItem key={vehicle} value={vehicle} className="text-white hover:bg-zinc-700">
                                {vehicle}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="-" className="text-zinc-500">
                              Keine Fahrzeuge konfiguriert
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-[#1F2937]">
            <Button
              variant="outline"
              onClick={() => setShowParticipationDialog(false)}
              className="border-[#1F2937]/80"
            >
              Abbrechen
            </Button>
            <Button
              onClick={saveParticipationWithDetails}
              className="bg-[#DC2626] hover:bg-[#DC2626]/90"
            >
              Speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={(open) => {
        setShowForgotPassword(open);
        if (!open) {
          setResetStep("request");
          setResetEmail("");
          setResetToken("");
          setNewPassword("");
          setConfirmPassword("");
          setResetMessage("");
        }
      }}>
        <DialogContent className="bg-[#1F2937] border-[#DC2626]/30">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-[#FF8C00]" />
              {resetStep === "request" ? "Passwort vergessen?" : "Neues Passwort setzen"}
            </DialogTitle>
          </DialogHeader>
          
          {resetStep === "request" ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <p className="text-sm text-zinc-400">
                Geben Sie Ihre E-Mail-Adresse ein. Sie erhalten einen Link zum Zurücksetzen Ihres Passworts.
              </p>
              <div className="space-y-2">
                <Label>E-Mail-Adresse</Label>
                <Input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="ihre@email.de"
                  className="bg-[#1F2937]/50 border-[#DC2626]/30 focus:border-[#DC2626]"
                  required
                />
              </div>
              {resetMessage && (
                <div className="p-3 bg-[#22c55e]/20 border border-[#22c55e]/50 rounded text-[#22c55e] text-sm">
                  {resetMessage}
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForgotPassword(false)}
                  className="flex-1 border-zinc-600"
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  disabled={resetLoading || !resetEmail}
                  className="flex-1 bg-gradient-to-r from-[#DC2626] to-[#FF8C00]"
                >
                  {resetLoading ? "Senden..." : "Link senden"}
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <p className="text-sm text-zinc-400">
                Geben Sie Ihr neues Passwort ein.
              </p>
              <div className="space-y-2">
                <Label>Neues Passwort</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min. 6 Zeichen"
                  className="bg-[#1F2937]/50 border-[#DC2626]/30 focus:border-[#DC2626]"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Passwort bestätigen</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Passwort wiederholen"
                  className="bg-[#1F2937]/50 border-[#DC2626]/30 focus:border-[#DC2626]"
                  required
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForgotPassword(false)}
                  className="flex-1 border-zinc-600"
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  disabled={resetLoading || !newPassword || !confirmPassword}
                  className="flex-1 bg-gradient-to-r from-[#DC2626] to-[#FF8C00]"
                >
                  {resetLoading ? "Speichern..." : "Passwort ändern"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default App;
